import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { updateAppState } from "../../actions/appstate";
import { UPDATE_STATE } from "../../actions/types";
import { APP_CACHE } from "../../app/config";
import { getFromSession, getTileById } from "../../app/util";
import { useProductVariant } from "../../hooks/use-product-variant";
import { streamChat, listSessions, getSession, clearSessions, sendFeedback, getAbConfig, rateEval, getInsightDisplay, saveInsightDisplay, sendSupportRequest, getCapabilities, getBriefingPref, saveBriefingPref, getBriefing, streamBriefing, sendBriefingFeedback, getPortfolios } from "../../services/wizard.service";
import { registryFor, GRAIN_TO_SEGMENT, mergeTilesForSources, widestRange } from "./sourceRegistry";
import { describeView } from "./viewLabel";
import CalculatorCard from "./CalculatorCard";
import MathPadCard from "./MathPadCard";
import RateSimulatorCard from "./RateSimulatorCard";
import useVoiceInput from "./useVoiceInput";
import fixTranscript from "./voiceVocabulary";
import DemandCalendarCard from "./DemandCalendarCard";
import PickupSensitivityCard from "./PickupSensitivityCard";
import GroupQuoteCard from "./GroupQuoteCard";
import BriefingCard from "./BriefingCard";
import PortfolioBriefingCard from "./PortfolioBriefingCard";
import "./LodgiqWizard.scss";

/* ------------------------------------------------------------------
   Ask LodgIQ — Context-aware revenue co-pilot
   Floating launcher + streaming chat window.
   Talks to POST /rms/properties/chat/<property_id>/ (SSE) and the
   /sessions/ endpoints for history. Branding in LodgiqWizard.scss.
------------------------------------------------------------------- */

/* --- Inline SVG icons (no extra deps) --- */
const Icon = {
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  ),
  // moon/sun are intentionally retained though currently unreferenced: the dark
  // theme is built and styled, only its entry point is removed until the main
  // app has one too (AL-91).
  // History: a clock alone reads as "time/schedule" in a product full of dates —
  // the rewind arc is the unambiguous "past items" convention (AL-91).
  history: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.2 12a8.8 8.8 0 1 0 2.6-6.2" />
      <path d="M3 4v4.2h4.2" />
      <path d="M12 7.6V12l3 1.8" />
    </svg>
  ),
  // Window SIZE glyphs (AL-91): the panel has THREE states, and the shrink
  // direction would need a down-left arrow — which the brand arrow rule forbids.
  // So these depict the RESULTING LAYOUT instead of a direction, and the active
  // one is filled, making the row a position indicator. Filled rects also stay
  // legible at 15px, where arrowheads turn to mush.
  sizeHalf: (
    <svg viewBox="0 0 16 16" fill="none">
      <rect x="1.4" y="2.4" width="13.2" height="11.2" rx="1.6" stroke="currentColor" strokeWidth="1.3" opacity=".55" />
      <rect x="1.4" y="8" width="13.2" height="5.6" rx="1.6" fill="currentColor" />
    </svg>
  ),
  sizeDocked: (
    <svg viewBox="0 0 16 16" fill="none">
      <rect x="1.4" y="2.4" width="13.2" height="11.2" rx="1.6" stroke="currentColor" strokeWidth="1.3" opacity=".55" />
      <rect x="8.4" y="2.4" width="6.2" height="11.2" rx="1.6" fill="currentColor" />
    </svg>
  ),
  sizeFull: (
    <svg viewBox="0 0 16 16" fill="none">
      <rect x="1.4" y="2.4" width="13.2" height="11.2" rx="1.6" fill="currentColor" />
    </svg>
  ),
  maximize: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
    </svg>
  ),
  minimize: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /></svg>
  ),
  restore: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="8" width="12" height="12" rx="1.5" /><path d="M4 16V5.5A1.5 1.5 0 0 1 5.5 4H16" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
  ),
  pencil: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  send: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
  ),
  moon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
  ),
  copy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
  ),
  dots: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
    </svg>
  ),
  // Two stacked speech bubbles = "both sides of the exchange". Deliberately NOT
  // a second pair of copy sheets: it sits next to Copy and must not read as it.
  messages: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h2v3l3.2-3H14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" />
      <path d="M18 9h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-2v3l-3.2-3" />
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ),
  spark: (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.9 5.6L19.5 9l-4.3 3.2L16.6 18 12 14.7 7.4 18l1.4-5.8L4.5 9l5.6-1.4z" /></svg>
  ),
  thumbUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3zM7 10l4.2-7a2.3 2.3 0 0 1 2.3 2.3V9h5.1a2 2 0 0 1 2 2.4l-1.4 7a2 2 0 0 1-2 1.6H7" />
    </svg>
  ),
  thumbDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3zM17 14l-4.2 7a2.3 2.3 0 0 1-2.3-2.3V15H5.4a2 2 0 0 1-2-2.4l1.4-7a2 2 0 0 1 2-1.6H17" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
  ),
  // AL-94: several buildings — the portfolio scope glyph. Used in the header
  // pill, the scope strip and on every answer produced in portfolio scope.
  buildings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V8l5-3v16M14 21V11l5-2.5V21" />
      <path d="M8 11h.01M8 14h.01M8 17h.01M17 14h.01M17 17h.01" />
    </svg>
  ),
  columns: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="7" height="16" rx="1" /><rect x="14" y="4" width="7" height="16" rx="1" />
    </svg>
  ),
  link: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M17 7H8M17 7v9" /></svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v11M7 10l5 5 5-5" /><path d="M4 19h16" />
    </svg>
  ),
  // INTERNAL AB indicator glyphs (never labelled on screen). Wrench = agent engine
  // (fetched tools live), stacked layers = context engine (answered from the bundle).
  engineAgent: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a3.9 3.9 0 0 0-5.1 5.1L3 18l3 3 6.6-6.6a3.9 3.9 0 0 0 5.1-5.1l-2.6 2.6-2.5-.4-.4-2.5 2.5-2.7z" />
    </svg>
  ),
  engineContext: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 12l9 5 9-5" /><path d="M3 16l9 5 9-5" />
    </svg>
  ),
  gear: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.09a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.09a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1z" />
    </svg>
  ),
  // Workspace-style window controls (same glyphs as the data-level expand control).
  expandOut: (
    <svg viewBox="0 0 33 33" fill="currentColor">
      <path d="M13.8155 10H10.3543C10.1581 10 10 10.1581 10 10.3543V13.8101C10 14.1317 10.3816 14.2897 10.6105 14.0662L11.9514 12.7254L15.1182 15.8922C15.3308 16.1048 15.6742 16.1048 15.8868 15.8922C16.0994 15.6797 16.0994 15.3363 15.8868 15.1237L12.7199 11.9514L14.0608 10.6105C14.2897 10.3816 14.1317 10 13.8155 10Z" />
      <path d="M23 19.1899V22.6457C23 22.8419 22.8419 23 22.6457 23H19.1899C18.8738 23 18.7157 22.6184 18.9392 22.3895L20.2801 21.0486L17.1132 17.8818C16.9006 17.6692 16.9006 17.3258 17.1132 17.1132C17.3258 16.9006 17.6692 16.9006 17.8818 17.1132L21.0486 20.2801L22.3895 18.9392C22.613 18.7157 23 18.8738 23 19.1899Z" />
      <path d="M10 19.1899V22.6457C10 22.8419 10.1581 23 10.3543 23H13.8101C14.1262 23 14.2843 22.6184 14.0608 22.3895L12.7199 21.0486L15.8868 17.8818C16.0994 17.6692 16.0994 17.3258 15.8868 17.1132C15.6742 16.9006 15.3308 16.9006 15.1182 17.1132L11.9514 20.2801L10.6105 18.9392C10.387 18.7157 10 18.8738 10 19.1899Z" />
      <path d="M19.1899 10H22.6457C22.8419 10 23 10.1581 23 10.3543V13.8101C23 14.1262 22.6184 14.2843 22.3895 14.0608L21.0486 12.7199L17.8818 15.8868C17.6692 16.0994 17.3258 16.0994 17.1132 15.8868C16.9006 15.6742 16.9006 15.3308 17.1132 15.1182L20.2801 11.9514L18.9392 10.6105C18.7157 10.387 18.8738 10 19.1899 10Z" />
    </svg>
  ),
  collapseIn: (
    <svg viewBox="0 0 33 33" fill="currentColor">
      <path d="M15.2897 11.5105V14.9405C15.2897 15.1305 15.1297 15.2905 14.9397 15.2905H11.5197C11.2097 15.2905 11.0497 14.9105 11.2697 14.6805L12.5997 13.3505L9.44973 10.2205C9.23973 10.0005 9.23973 9.66047 9.44973 9.45047C9.65973 9.24047 9.99973 9.24047 10.2197 9.45047L13.3597 12.5905L14.6897 11.2605C14.9097 11.0405 15.2997 11.2005 15.2997 11.5105H15.2897Z" />
      <path d="M18.0697 17.7105C17.8797 17.7105 17.7197 17.8705 17.7197 18.0605V21.4905C17.7197 21.8005 18.0997 21.9605 18.3297 21.7405L19.6597 20.4105L22.7997 23.5505C23.0097 23.7605 23.3497 23.7605 23.5597 23.5505C23.7697 23.3405 23.7697 23.0005 23.5597 22.7905L20.4197 19.6505L21.7497 18.3205C21.9697 18.1005 21.8097 17.7105 21.4997 17.7105H18.0697Z" />
      <path d="M14.9297 17.7105C15.1197 17.7105 15.2797 17.8705 15.2797 18.0605V21.4905C15.2797 21.8005 14.8997 21.9605 14.6697 21.7405L13.3397 20.4105L10.1997 23.5505C9.98973 23.7605 9.64973 23.7605 9.43973 23.5505C9.22973 23.3405 9.22973 23.0005 9.43973 22.7905L12.5797 19.6505L11.2497 18.3205C11.0297 18.1005 11.1897 17.7105 11.4997 17.7105H14.9297Z" />
      <path d="M17.7097 11.5105V14.9405C17.7097 15.1305 17.8697 15.2905 18.0597 15.2905H21.4797C21.7897 15.2905 21.9497 14.9105 21.7297 14.6805L20.3997 13.3505L23.5397 10.2105C23.7497 10.0005 23.7497 9.66047 23.5397 9.45047C23.3297 9.24047 22.9897 9.24047 22.7797 9.45047L19.6397 12.5905L18.3097 11.2605C18.0897 11.0405 17.6997 11.2005 17.6997 11.5105H17.7097Z" />
    </svg>
  ),
  // LodgIQ brand arrow (from LodgIQ™_Arrow_LightBlue — official mark, tinted via currentColor)
  lodgiqArrow: (
    <svg viewBox="0 0 36 36" fill="currentColor">
      <path d="M 2.328125 1 L 2.328125 10.601562 L 18.558594 10.601562 L 0.9375 28.222656 L 7.734375 35.015625 L 25.375 17.371094 L 25.375 33.636719 L 34.972656 33.636719 L 34.972656 1 Z M 2.328125 1" />
    </svg>
  ),
  // Ask LodgIQ feature mark: AI sparkles in brand colors — same glyph as the
  // workspace "Summarize this view" trigger, so the feature has ONE identity.
  aiSparkles: (
    <svg viewBox="6 6 21 19">
      <path d="M14.5 8.5 C15.3 13.2 16.9 14.8 21.6 15.6 C16.9 16.4 15.3 18 14.5 22.7 C13.7 18 12.1 16.4 7.4 15.6 C12.1 14.8 13.7 13.2 14.5 8.5 Z" fill="#01a4ef" />
      <path d="M22.3 8 C22.7 10.3 23.5 11.1 25.8 11.5 C23.5 11.9 22.7 12.7 22.3 15 C21.9 12.7 21.1 11.9 18.8 11.5 C21.1 11.1 21.9 10.3 22.3 8 Z" fill="#9ed00d" />
      <path d="M21 18.6 C21.3 20.2 21.8 20.7 23.4 21 C21.8 21.3 21.3 21.8 21 23.4 C20.7 21.8 20.2 21.3 18.6 21 C20.2 20.7 20.7 20.2 21 18.6 Z" fill="#01a4ef" opacity="0.55" />
    </svg>
  ),
  // Single 4-point sparkle (currentColor) — for tinted contexts (white on the launcher).
  sparkle: (
    <svg viewBox="6 6 21 19" fill="currentColor">
      <path d="M14.5 8.5 C15.3 13.2 16.9 14.8 21.6 15.6 C16.9 16.4 15.3 18 14.5 22.7 C13.7 18 12.1 16.4 7.4 15.6 C12.1 14.8 13.7 13.2 14.5 8.5 Z" />
      <path d="M22.3 8 C22.7 10.3 23.5 11.1 25.8 11.5 C23.5 11.9 22.7 12.7 22.3 15 C21.9 12.7 21.1 11.9 18.8 11.5 C21.1 11.1 21.9 10.3 22.3 8 Z" opacity="0.75" />
    </svg>
  ),
  // LodgIQ dual-arrow logo: large green arrow + smaller blue arrow (brand colors).
  lodgiqMark: (
    <svg viewBox="0 0 62 72" fill="none">
      <g fill="#9ed00d" transform="scale(1.32)">
        <path d="M 2.328125 1 L 2.328125 10.601562 L 18.558594 10.601562 L 0.9375 28.222656 L 7.734375 35.015625 L 25.375 17.371094 L 25.375 33.636719 L 34.972656 33.636719 L 34.972656 1 Z M 2.328125 1" />
      </g>
      <g fill="#01a4ef" transform="translate(25 34) scale(1.02)">
        <path d="M 2.328125 1 L 2.328125 10.601562 L 18.558594 10.601562 L 0.9375 28.222656 L 7.734375 35.015625 L 25.375 17.371094 L 25.375 33.636719 L 34.972656 33.636719 L 34.972656 1 Z M 2.328125 1" />
      </g>
    </svg>
  ),
  expandUp: (
    <svg viewBox="0 0 33 33" fill="currentColor">
      <path d="M13.8155 8.5H10.3543C10.1581 8.5 10 8.6581 10 8.8543V12.3101C10 12.6317 10.3816 12.7897 10.6105 12.5662L11.9514 11.2254L15.1182 14.3922C15.3308 14.6048 15.6742 14.6048 15.8868 14.3922C16.0994 14.1797 16.0994 13.8363 15.8868 13.6237L12.7199 10.4514L14.0608 9.1105C14.2897 8.8816 14.1317 8.5 13.8155 8.5Z" />
      <path d="M19.1899 8.5H22.6457C22.8419 8.5 23 8.6581 23 8.8543V12.3101C23 12.6262 22.6184 12.7843 22.3895 12.5608L21.0486 11.2199L17.8818 14.3868C17.6692 14.5994 17.3258 14.5994 17.1132 14.3868C16.9006 14.1742 16.9006 13.8308 17.1132 13.6182L20.2801 10.4514L18.9392 9.1105C18.7157 8.887 18.8738 8.5 19.1899 8.5Z" />
      <path d="M9.5 20.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  ),
};

// Tiny, unlabelled engine indicator shown before Copy on every answer, for everyone.
// Deliberately NO tooltip — it's a faint glyph only we can decode (wrench = agent,
// layers = context); customers see a neutral mark with no explanation.
// "insight" (Summarize-this-view) reuses the context glyph; canned/external get none.
const ENGINE_GLYPH = {
  agent: { icon: Icon.engineAgent },
  context: { icon: Icon.engineContext },
  insight: { icon: Icon.engineContext },
};

// The interactive tools, surfaced in the empty state and the composer "+" menu
// so users can DISCOVER them (they also open naturally from plain questions).
// `scope` says where a tool can honestly run.
//   "property" — needs ONE hotel. Every tool here is currently property-scoped,
//                the revenue calculator included: displacement, break-even and
//                pickup are that hotel's capacity, rates and forecast, not free
//                arithmetic. In portfolio mode they are hidden rather than
//                shown-then-refused — a tool whose only possible reply is "which
//                hotel?" is a dead end, and a list of 100 is not a menu.
//   "portfolio" — cross-hotel. None built yet (portfolio demand and group
//                placement are next); they appear here when they exist.
const TOOL_MENU = [
  {
    key: "calendar",
    scope: "property",
    label: "Demand calendar",
    desc: "How's the month shaping up",
    prompt: "Show me the demand calendar for the next 30 days",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="5" width="16" height="16" rx="2.5" /><path d="M8 3v4M16 3v4M4 10h16" />
      </svg>
    ),
  },
  {
    key: "simulator",
    scope: "property",
    label: "Rate simulator",
    desc: "Test a rate move on a date",
    prompt: "Open the rate simulator for this Saturday",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 17l5-6 4 3 7-9" /><path d="M14 5h6v6" />
      </svg>
    ),
  },
  {
    key: "calculator",
    scope: "property",
    label: "Revenue calculator",
    desc: "Displacement, break-even, pickup…",
    prompt: "Open the revenue calculator",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="3" width="14" height="18" rx="2.5" /><path d="M8 7h8" />
        <path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01" />
      </svg>
    ),
  },
  {
    key: "quote",
    scope: "property",
    label: "Group quote",
    desc: "Evaluate a group and draft the proposal",
    prompt: "I have a group ask — help me evaluate it and draft a quote",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5M9 13h6M9 17h4" />
      </svg>
    ),
  },
];

/* Faint flowing background lines (green + blue) like the mockup */
function BgLines() {
  return (
    <svg className="wiz-bg-lines" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g fill="none" strokeWidth="2">
        <path d="M-50 120 C 300 60, 600 420, 1250 240" stroke="#cfe6f7" opacity="0.55" />
        <path d="M-50 260 C 350 200, 700 560, 1250 380" stroke="#e7f2c4" opacity="0.6" />
        <path d="M-50 420 C 300 380, 750 720, 1250 560" stroke="#d6ecfa" opacity="0.5" />
        <path d="M-50 600 C 400 540, 800 860, 1250 700" stroke="#eef6d0" opacity="0.55" />
      </g>
    </svg>
  );
}

/* Lightweight markdown renderer for the backend's output:
   #/## headings · **bold** · "- " bullets · pipe-delimited tables. */
function inline(str) {
  const parts = str.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
  );
}

/* --- Chart support: turn a markdown table into an optional Highcharts view --- */

// Split a markdown table row into cells, dropping the empty cell that a leading/
// trailing "|" would otherwise create (so "| Date | OTB |" -> ["Date","OTB"], not
// ["", "Date", "OTB", ""]). Without this the first real column becomes a phantom
// x-axis and the Date column gets mis-read as a numeric series.
function splitTableRow(line) {
  let s = String(line).trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

// Parse a table cell into a number, tolerating $, %, commas, and (parenthesised) negatives.
function parseNum(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s || s === "-" || s === "—" || /^n\/?a$/i.test(s)) return null;
  const neg = /^\(.*\)$/.test(s);
  s = s.replace(/[(),$%\s]/g, "").replace(/[^0-9.-]/g, "");
  if (s === "" || s === "-" || s === ".") return null;
  const n = parseFloat(s);
  if (isNaN(n)) return null;
  return neg ? -n : n;
}

// A row the model pre-aggregated ("Total", "Period Total", "Grand Total",
// "Subtotal", "Sum", an average) is not a data row: it must not render as one,
// must not feed the chart, and must not be summed by analyzeTotals — summing it
// double-counts every additive column. That is exactly how one "**Period Total**"
// row of 20,957 rooms became a "Total · 18 rows" footer of 41,914. The .replace
// drops any "**" so a bolded "**Period Total**" still matches.
// Does a LABEL read as a pre-aggregated total? Catches "Total", "Grand/Period/Sub/
// Running/Net Total", and any qualifier + total the model invents — "Week Total",
// "Monthly Total", "November Total", "Totals", plus "Sum"/"Average". The word
// "total" must END the label (bar a trailing ":" or "(Nov 1-7)" qualifier), so a
// data row/segment named "Total Rewards" — total at the FRONT with more after it —
// is NOT treated as a total and survives.
function looksLikeTotalLabel(s) {
  const t = String(s || "").replace(/\*/g, "").trim();
  if (!t) return false;
  if (/\btotals?\b\s*:?\s*(\([^)]*\))?\s*$/i.test(t)) return true;
  return /^(sub-?totals?|sums?|averages?|avg|mean)\b/i.test(t);
}

function isSummaryRow(cells) {
  return looksLikeTotalLabel((cells && cells[0]) || "");
}

// The same restatement can arrive NOT as a table row but as a standalone line —
// "**Period totals (Nov 1-7): 667 rooms | 48.54% occupancy | $546.68 ADR ...**",
// whether rendered as a one-row table or a bold sentence. It is redundant with the
// footer the UI computes and has shipped CONFLICTING numbers (its ADR/RevPAR were
// hand-computed and disagreed with the weighted footer), so drop it. Tightly
// scoped: the LEAD of the line must read as a total label AND the line must be a
// metrics dump (>=2 metric keywords), so ordinary prose ("the week total was
// strong, with revenue climbing") survives.
function isSummaryLine(text) {
  const t = String(text || "").replace(/\*/g, "").trim();
  const lead = t.split(/[:\-–—(|]/)[0].trim();   // "Week total" from "Week total (Nov 1-7): ..."
  if (!looksLikeTotalLabel(lead)) return false;
  const metrics = (t.match(/\b(rooms?|occupancy|occ|revenue|adr|revpar)\b/gi) || []).length;
  return metrics >= 2;
}

function isDateLike(v) {
  const s = String(v || "").trim();
  if (!s) return false;
  if (/^\d{4}-\d{1,2}(-\d{1,2})?$/.test(s)) return true;            // 2026-07-01
  if (/^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(s)) return true;         // 7/1 or 07/01/26
  if (/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(s) && /\d/.test(s)) return true;
  return false;
}

// Decide whether a parsed table can be charted, and extract labels + numeric series.
function analyzeTable(head, rows) {
  if (!head || head.length < 2 || !rows || rows.length < 2) return { chartable: false };
  const need = Math.max(2, Math.ceil(rows.length * 0.6));
  const numericCols = [];
  for (let c = 1; c < head.length; c++) {
    let ok = 0;
    rows.forEach((r) => { if (c < r.length && parseNum(r[c]) != null) ok += 1; });
    if (ok >= need) numericCols.push(c);
  }
  if (!numericCols.length) return { chartable: false };
  const labels = rows.map((r) => (r[0] != null ? String(r[0]) : ""));
  const dateLike = labels.filter(isDateLike).length >= Math.ceil(labels.length * 0.6);
  const series = numericCols.map((c) => ({ name: head[c], data: rows.map((r) => parseNum(r[c])) }));
  return { chartable: true, labels, series, dateLike };
}

/* --- Totals row -------------------------------------------------------------

   A footer row on any table whose columns can honestly be aggregated. Three
   rules do all the work, and two of them are refusals:

   1. COUNTS ADD. Rooms, room-nights, pickup, bookings, revenue — these are
      quantities, and summing them across stay dates or across hotels is the
      answer a reader is doing in their head anyway.

   2. RATIOS ARE WEIGHTED, NEVER AVERAGED. Occupancy is total rooms over total
      capacity; ADR is total revenue over total rooms; RevPAR is revenue over
      capacity. The mean of a percentage column is a different number and it is
      always wrong — one 500-room hotel and one 40-room hotel do not each carry
      half the portfolio's occupancy. We can only weight when the weight column
      is present in the SAME table, so when it isn't, we print an em dash rather
      than the tempting-but-false average.

   3. SOME COLUMNS HAVE NO TOTAL AT ALL. A column of sell rates, BARs or index
      values has no meaningful sum: adding Tuesday's rate to Wednesday's answers
      no question. Dates, hotel names and variance percentages likewise.

   The label carries the row count ("Total · 7 rows") because this component only
   ever sees the rows it was handed. The pickup answer that prompted this showed
   the seven largest gains out of thirty-one stay dates; a row saying plain
   "Total" under those seven would have contradicted the +105 in the sentence
   directly above it. Naming the row count can never do that.                   */

// Money in more than one currency must never be added. Portfolio tables are the
// reason: no FX rate is applied anywhere in this product, so a column holding
// both $ and £ has no total, only a category error.
function currencyOf(raw) {
  const m = String(raw == null ? "" : raw).match(/[$£€¥₹]|\b(USD|EUR|GBP|JPY|INR|AUD|CAD|MXN|SGD|AED)\b/i);
  return m ? m[0].toUpperCase() : null;
}

// What kind of total, if any, does this column take? Order matters: RevPAR and
// ADR are tested before the generic "rev"/"rate" patterns they contain.
function columnAgg(header) {
  const h = String(header || "").toLowerCase().trim();
  if (!h) return "none";
  // A DELTA column is not the metric it names. "Pickup Occ Pts" is a change in
  // percentage POINTS, not an occupancy — weighting it like occupancy returns the
  // occupancy itself, which is how a totals row ends up printing 84.9 twice.
  // Tested before the plain metric patterns, because the delta word usually sits
  // beside the metric word ("pickup occ", "ADR change").
  const isDelta = /pickup|pick[-\s]?up|chang|delta|gain|movement|\bpts?\b|points|\bvar\b|varian|\bvs\b/.test(h);
  if (isDelta && /\bocc\b|occupancy/.test(h)) return "occ_delta";
  if (isDelta && /\badr\b|average\s*rate/.test(h)) return "adr_delta";
  if (isDelta && /revpar|rate/.test(h)) return "none";   // no honest weight for these
  if (/revpar|rev\s*par/.test(h)) return "revpar";
  if (/\badr\b|average\s*(daily\s*)?rate|avg\s*rate/.test(h)) return "adr";
  if (/\bocc\b|occupancy/.test(h)) return "occ";
  // Explicit refusals. An index, a position, a variance or a plain rate/price
  // column is not additive, and neither is anything that identifies a row.
  if (/index|position|\bvs\b|varian|\bvar\b|chang|delta|\bdiff|growth|\bsdly\b|\blast year\b|\bly\b/.test(h)) return "none";
  if (/\brate\b|\bbar\b|price|\brec\b|ceiling|floor|threshold/.test(h)) return "none";
  if (/date|^day$|\bdow\b|hotel|propert|name|status|segment|market|type|note|action|owner|source|reason|room type|channel/.test(h)) return "none";
  // Quantities.
  if (/rooms?|room[-\s]?nights?|\brn\b|\botb\b|pickup|pick[-\s]?up|bookings?|reservations?|blocks?|\bnights?\b|count|arrivals?|departures?|cancel|displac|\bpax\b/.test(h)) return "sum";
  if (/revenue|\brev\b|\bamount\b|\btotal\b|\bvalue\b|contribution/.test(h)) return "sum";
  return "none";      // unknown header: refuse rather than guess
}

const MIN_TOTAL_ROWS = 3;          // two rows is a comparison, not something to total
const NUMERIC_COVERAGE = 0.6;      // a column must mostly parse before we total it

// Build the footer. Returns null when nothing in the table can honestly be added.
function analyzeTotals(head, rows) {
  if (!head || !rows || rows.length < MIN_TOTAL_ROWS) return null;

  // TRANSPOSED tables have no totals. When the first column holds METRIC NAMES —
  // Date, Rooms on Books, Occupancy, ADR, RevPAR down the rows and one "Value"
  // column beside them — each row is a different unit and the column is not a
  // quantity. Summing it produced "Total · 5 rows  $3673.6": rooms plus a
  // percentage plus two rates plus the YEAR out of a parsed date.
  if (/^\s*(metric|measure|kpi|field|item|name|attribute|indicator)s?\s*$/i.test(String(head[0] || ""))) {
    return null;
  }

  const need = Math.ceil(rows.length * NUMERIC_COVERAGE);
  const kinds = head.map((h) => columnAgg(h));
  // A DATE IS NOT A QUANTITY. parseNum("2026-08-28") happily returns 2026, which
  // is how a year ended up inside a money total. Blank them before anything
  // counts coverage or adds them up.
  const nums = head.map((_, c) =>
    rows.map((r) => (c < r.length && !isDateLike(r[c]) ? parseNum(r[c]) : null)));
  const parsed = nums.map((col) => col.filter((v) => v != null).length);

  // A column we meant to total but which doesn't actually hold numbers is not a
  // total — it's a header that happened to match.
  kinds.forEach((k, c) => { if (k !== "none" && parsed[c] < need) kinds[c] = "none"; });

  // Mixed currency kills the total for EVERY money figure, not just sums. A
  // weighted ADR across $ and £ is an implicit FX rate — the same error as
  // adding them, wearing a division sign. Portfolios are single-currency by
  // rule now, but the table can come from anywhere and this side can't know the
  // rule held, so it fails safe.
  // MIXED UNITS IN ONE COLUMN = not one quantity. A column holding "90.6%",
  // "$184" and "1,206" is a list of different things that happen to be numbers;
  // adding them answers nothing. This is the general form of the transposed-table
  // trap above, and it also catches a "Value" column that slipped through.
  kinds.forEach((k, c) => {
    if (k === "none") return;
    const units = new Set();
    rows.forEach((r) => {
      const raw = String(r[c] == null ? "" : r[c]).trim();
      if (!raw || parseNum(raw) == null) return;
      units.add(/%/.test(raw) ? "pct" : (currencyOf(raw) ? "money" : "plain"));
    });
    if (units.size > 1) kinds[c] = "none";
  });

  const MONEY_KINDS = new Set(["sum", "adr", "adr_delta", "revpar"]);
  kinds.forEach((k, c) => {
    if (!MONEY_KINDS.has(k)) return;
    const seen = new Set();
    rows.forEach((r) => { const cur = currencyOf(r[c]); if (cur) seen.add(cur); });
    if (seen.size > 1) kinds[c] = "none";
  });

  const idx = (kind) => kinds.findIndex((k) => k === kind);

  // TOTAL REVENUE — the numerator for both ADR and RevPAR, in preference order.
  // A stated Revenue column is the exact figure; reconstructing it as
  // rooms x ADR re-derives it from an ADR already rounded to 2dp and drifts a
  // little further with every row. Third option unlocks a table that carries
  // RevPAR but no ADR, which used to get an em dash for no good reason.
  const revenueTotal = (roomsCol, adrCol, revparCol, capacityTotal) => {
    // The revenue that feeds ADR and RevPAR is the revenue ON THE BOOKS, never a
    // DELTA. "Pickup Revenue" matches /revenue/ but is the change over a window;
    // using it made ADR read $33 against rows of $299–$663, because it divided
    // one week's gain by the whole period's rooms.
    const revCol = kinds.findIndex((k, c) =>
      k === "sum"
      && /revenue|^rev\b|\brev$/i.test(head[c])
      && !/pickup|pick[-\s]?up|chang|delta|gain|\bvar\b|varian|\bvs\b/i.test(head[c]));
    if (revCol >= 0) return nums[revCol].reduce((a, v) => a + (v || 0), 0);
    if (roomsCol >= 0 && adrCol >= 0) {
      let rev = 0;
      rows.forEach((r, i) => {
        const rooms = nums[roomsCol][i], adr = nums[adrCol][i];
        if (rooms != null && adr != null) rev += rooms * adr;
      });
      return rev;
    }
    if (revparCol >= 0 && capacityTotal) {
      // Weighted the same way occupancy is: RevPAR is revenue per available
      // room, so revenue is RevPAR x capacity.
      let rev = 0, ok = 0;
      rows.forEach((r, i) => {
        const rp = nums[revparCol][i];
        if (rp != null) { rev += rp * (capacityTotal / rows.length); ok += 1; }
      });
      return ok === rows.length ? rev : null;
    }
    return null;
  };
  const roomsCol = kinds.findIndex((k, c) => k === "sum" && /rooms?\b|\botb\b|room[-\s]?nights?|\brn\b/i.test(head[c]) && !/pickup|pick[-\s]?up/i.test(head[c]));
  const occCol = idx("occ");
  const adrCol = idx("adr");

  const sumOf = (c) => nums[c].reduce((a, v) => (v == null ? a : a + v), 0);

  // TOTAL CAPACITY — the denominator for occupancy and RevPAR. Occupancy is
  // total rooms sold over total capacity; it is never the mean of a percentage
  // column. Getting the numerator right and the denominator wrong is the same
  // error wearing a disguise.
  //
  // If the table states capacity, use it. Otherwise recover it per row: a night
  // with 104 rooms at 52.8% was selling into 197. That recovery is impossible
  // for a night with NOTHING on the books — 0/0 — and those nights are common at
  // the far end of any forward-looking window. Dropping them was silently
  // shrinking the denominator and overstating occupancy: 50 rooms across one
  // 100-room night plus one empty one is 25%, not 50%.
  // A stated capacity column is only trustworthy when there is exactly ONE. A
  // dynamic-inventory answer carries both "Physical Capacity" and "Sellable
  // Capacity", and picking the first match silently totalled occupancy against
  // physical rooms while every row above showed it against sellable — 83.7%
  // under a column of 97.8%, 98.8%, 66.3%.
  //
  // With two candidates, deriving from the occupancy column is not a fallback,
  // it is the BETTER answer: rooms / occ% recovers whichever capacity that
  // column was actually computed against, so the total can never contradict the
  // rows it sits under.
  // Filtered on "is it fully numeric", NOT on `kinds` — a capacity column is
  // correctly classified "none" (it is a denominator, not a metric to total),
  // so testing kinds here would discard the very column we want.
  const capCols = head
    .map((h, i) => (/capacit|inventory|rooms? available|available rooms?/i.test(String(h || "")) ? i : -1))
    .filter((i) => i >= 0 && parsed[i] >= rows.length);
  const occCols = kinds.reduce((acc, k, i) => (k === "occ" ? [...acc, i] : acc), []);
  // With MORE THAN ONE occupancy column the table is showing two bases —
  // "Occ % (Physical)" and "Occ % (Sellable)" — and they have different
  // denominators by definition. A single shared capacity made both totals
  // identical (83.3% under a Sellable column whose rows run 4 points higher).
  // So each occupancy column derives its OWN capacity from itself, and a stated
  // capacity column is ignored, because it can only match one of the bases.
  const capCol = (capCols.length === 1 && occCols.length <= 1) ? capCols[0] : -1;

  const capMemo = new Map();
  const capacityFrom = (occIdx) => {
    if (capMemo.has(occIdx)) return capMemo.get(occIdx);
    let cap = null;
    if (capCol >= 0) {
      cap = nums[capCol].reduce((a, v) => a + (v || 0), 0);
    } else if (roomsCol >= 0 && occIdx >= 0) {
      const per = rows.map((r, i) => {
        const rooms = nums[roomsCol][i], occ = nums[occIdx][i];
        return (rooms == null || occ == null || occ <= 0) ? null : rooms / (occ / 100);
      });
      const known = per.filter((v) => v != null);
      if (known.length) {
        const lo = Math.min(...known), hi = Math.max(...known);
        if (hi - lo <= hi * 0.02) {
          // Capacity is effectively constant down the column — one hotel over a
          // date range. Safe to carry it onto the rows we couldn't derive, so an
          // empty night still contributes its rooms to the denominator.
          const flat = known.reduce((a, v) => a + v, 0) / known.length;
          cap = flat * rows.length;
        } else if (known.length === rows.length) {
          // Capacity genuinely varies — a portfolio table, one row per hotel. Only
          // safe when every row is known; imputing a "typical" hotel size here
          // would be inventing the denominator.
          cap = known.reduce((a, v) => a + v, 0);
        }
        // else: varying capacity AND missing rows -> no honest total.
      }
    }
    capMemo.set(occIdx, cap);
    return cap;
  };
  // The default denominator for anything that isn't itself an occupancy column
  // (RevPAR, pickup points): the FIRST occupancy basis shown.
  const capacity = capacityFrom(occCol);

  const cells = head.map((h, c) => {
    const kind = kinds[c];
    if (kind === "sum") return { text: fmtTotal(sumOf(c), nums[c], rows.map((r) => r[c])) };
    if (kind === "occ") {
      // capacityFrom(c), not the shared one: this column's own basis.
      const cap = capacityFrom(c);
      if (roomsCol < 0 || !cap) return { text: "—", why: "needs a rooms column to weight by" };
      return { text: fmtPct((sumOf(roomsCol) / cap) * 100, rows.map((r) => r[c])), weighted: true };
    }
    // Occupancy POINTS gained. Not a sum — adding each day's point movement
    // answers nothing — and not the occupancy weighting either. It is the total
    // rooms picked up over total capacity: 160 rooms into 2,955 available is
    // 5.4 points, whatever the daily swings were.
    // The rate ACHIEVED ON THE PICKUP: revenue gained over rooms gained. Weighted
    // by pickup rooms, exactly as ADR is weighted by rooms sold. Refused when net
    // pickup is zero or negative — cancellations outrunning bookings have no rate.
    if (kind === "adr_delta") {
      const puRooms = kinds.findIndex((k, i) =>
        k === "sum" && /pickup|pick[-\s]?up|gain/i.test(head[i]) && /rooms?\b/i.test(head[i]));
      const puRev = kinds.findIndex((k, i) =>
        k === "sum" && /pickup|pick[-\s]?up|gain/i.test(head[i]) && /revenue|\brev\b/i.test(head[i]));
      if (puRooms < 0 || puRev < 0) {
        return { text: "—", why: "needs pickup rooms and pickup revenue to weight by" };
      }
      const r = sumOf(puRooms);
      if (r <= 0) return { text: "—", why: "no net rooms picked up — no rate on the pickup" };
      return { text: fmtMoney(sumOf(puRev) / r, rows.map((x) => x[c])), weighted: true };
    }
    if (kind === "occ_delta") {
      const puRooms = kinds.findIndex((k, i) =>
        k === "sum" && /pickup|pick[-\s]?up|gain/i.test(head[i]) && /rooms?\b/i.test(head[i]));
      if (puRooms < 0 || !capacity) {
        return { text: "—", why: "needs a pickup-rooms column and capacity to weight by" };
      }
      return { text: fmtPct((sumOf(puRooms) / capacity) * 100, rows.map((r) => r[c])), weighted: true };
    }
    // ADR = TOTAL REVENUE / TOTAL ROOMS SOLD. Never the mean of the column: 100
    // rooms at $200 beside 20 at $400 is $233, not $300.
    if (kind === "adr") {
      if (roomsCol < 0) return { text: "—", why: "needs a rooms column to weight by" };
      const revenue = revenueTotal(roomsCol, c, idx("revpar"), capacity);
      const roomsTot = nums[roomsCol].reduce((a, v) => a + (v || 0), 0);
      if (revenue == null || !roomsTot) return { text: "—" };
      return { text: fmtMoney(revenue / roomsTot, rows.map((r) => r[c])), weighted: true };
    }
    // RevPAR = TOTAL REVENUE / TOTAL CAPACITY — revenue per AVAILABLE room, so
    // the denominator is capacity (empty nights included), not rooms sold.
    if (kind === "revpar") {
      if (!capacity) return { text: "—", why: "needs capacity, or rooms + occupancy to derive it" };
      const revenue = revenueTotal(roomsCol, adrCol, c, capacity);
      if (revenue == null) return { text: "—", why: "no revenue, ADR or RevPAR column to work from" };
      return { text: fmtMoney(revenue / capacity, rows.map((r) => r[c])), weighted: true };
    }
    return { text: "" };
  });

  if (!cells.some((c, i) => kinds[i] !== "none" && c.text && c.text !== "—")) return null;

  // The label goes in the first column that isn't itself being totalled, so it
  // can never overwrite a number.
  const labelCol = kinds.findIndex((k) => k === "none");
  const anyWeighted = cells.some((c) => c.weighted);
  return {
    cells, kinds,
    labelCol: labelCol < 0 ? 0 : labelCol,
    label: `Total · ${rows.length} row${rows.length === 1 ? "" : "s"}`,
    note: anyWeighted ? "Occupancy, ADR and RevPAR are weighted, not averaged." : "",
  };
}

// Match the precision the column already uses — a table of whole rooms should
// not sprout decimals in its footer.
function fmtTotal(v, parsedCol, rawCol) {
  const anyDecimal = parsedCol.some((n) => n != null && !Number.isInteger(n));
  const signed = (rawCol || []).some((s) => /^\s*\+/.test(String(s == null ? "" : s)));
  const cur = (rawCol || []).map(currencyOf).find(Boolean);
  const body = anyDecimal ? v.toFixed(1) : Math.round(v).toLocaleString();
  const sign = signed && v > 0 ? "+" : "";
  return `${cur && cur.length === 1 ? cur : ""}${sign}${body}`;
}
// Carry the column's own "%" AND its own decimal precision so the footer reads
// like the cells above it — a bare 84.9 under a column of 85.3% values looks like
// a different unit, and a 1dp total (92.6%) under 2dp cells (92.94%, 96.69%)
// looks like it disagrees with the rows it sits under.
function fmtPct(v, rawCol) {
  const strs = (rawCol || []).map((s) => String(s == null ? "" : s));
  const pct = strs.some((s) => /%/.test(s));
  const dp = strs.reduce((m, s) => {
    const d = s.match(/\.(\d+)/);
    return d ? Math.max(m, d[1].length) : m;
  }, 1);
  return `${v.toFixed(Math.min(dp, 2))}${pct ? "%" : ""}`;
}
function fmtMoney(v, rawCol) {
  const cur = (rawCol || []).map(currencyOf).find(Boolean);
  return `${cur && cur.length === 1 ? cur : ""}${v.toFixed(2)}`;
}

// Compact number for data labels: 1,234 / 12.5 / 0.83 — no clutter.
function fmtDataLabel(v) {
  if (v == null) return "";
  const abs = Math.abs(v);
  if (abs >= 1000) return Highcharts.numberFormat(v, 0);
  if (Number.isInteger(v)) return String(v);
  return Highcharts.numberFormat(v, abs < 10 ? 2 : 1);
}

function buildChartOptions(analysis, type, theme) {
  const dark = theme === "dark";
  const txt = dark ? "#c7ccd3" : "#48505a";
  const grid = dark ? "#2a313b" : "#ececf0";
  const labelColor = dark ? "#e7e9ec" : "#33404d";
  const outline = dark ? "1px contrast" : "1px #ffffff";
  return {
    chart: { type, height: 268, backgroundColor: "transparent", spacing: [12, 6, 6, 6], style: { fontFamily: "inherit" } },
    title: { text: undefined },
    credits: { enabled: false },
    colors: ["#01a4ef", "#9ed00d", "#1b3b6f", "#f5b301", "#e57373", "#8e6fd0"],
    legend: { enabled: analysis.series.length > 1, itemStyle: { color: txt, fontWeight: "500" }, itemHoverStyle: { color: txt } },
    xAxis: {
      categories: analysis.labels,
      labels: {
        autoRotation: [0, -30, -45, -60],   // rotate as the range gets longer instead of hiding labels
        style: { color: txt, fontSize: "10px" },
      },
      lineColor: grid, tickColor: grid,
    },
    yAxis: { title: { text: null }, labels: { style: { color: txt, fontSize: "10px" } }, gridLineColor: grid },
    tooltip: {
      shared: true, outside: true,
      // outside:true renders the tooltip on document.body — its container must sit
      // ABOVE the chat overlay (.wiz-overlay z-index: 13000) or it hides behind it.
      style: { zIndex: 13100 },
      pointFormatter: function () { return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${fmtDataLabel(this.y)}</b><br/>`; },
    },
    plotOptions: {
      series: {
        marker: { enabled: analysis.labels.length <= 24, radius: 3 },
        animation: false,
        // Value labels on every point; Highcharts hides overlapping ones automatically.
        dataLabels: {
          enabled: true,
          formatter: function () { return fmtDataLabel(this.y); },
          style: { color: labelColor, fontSize: "9px", fontWeight: "600", textOutline: outline },
          crop: false,
          overflow: "allow",
        },
      },
      column: { borderWidth: 0, groupPadding: 0.12, dataLabels: { inside: false } },
      line: { lineWidth: 2 },
    },
    series: analysis.series,
  };
}

// A rendered table that, when its data is numeric, offers an inline Table⇄Chart toggle.
// The chosen view is remembered across tables via localStorage ("wiz_tableview").
// A table is "wide" past this many columns: it can't be read in the mini panel
// without either shrinking the type or scrolling the key column out of view.
// Instead we keep the type size, pin the first column, and ask the panel to grow.
const WIDE_TABLE_COLS = 6;

function ChartableTable({ head, rows, theme }) {
  // A model-authored summary row ("Period Total", "Total", "Subtotal"…) is not a
  // data row: it must not feed the chart, and must not be summed by analyzeTotals
  // (doing so double-counted rooms/revenue). Split it out before anything reads rows.
  const dataRows = (rows || []).filter((r) => !isSummaryRow(r));
  const summaryRows = (rows || []).filter((r) => isSummaryRow(r));
  const analysis = analyzeTable(head, dataRows);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("wiz_tableview") === "chart" ? "chart" : "table"; } catch (_) { return "table"; }
  });
  const [ctype, setCtype] = useState(analysis.dateLike ? "line" : "column");
  const wide = (head || []).length > WIDE_TABLE_COLS;

  // Ask the wizard to expand when a wide table appears. Fired once per mount, and
  // only a REQUEST — the wizard ignores it if the user has already sized the panel
  // themselves, so this never fights a deliberate choice.
  useEffect(() => {
    if (!wide) return;
    try {
      document.dispatchEvent(new CustomEvent("wizWideTable", { detail: { cols: (head || []).length } }));
    } catch (_) {}
  }, [wide, head]);

  // Computed from the data rows only, so neither the chart series nor the footer
  // ever sees a pre-aggregated row.
  const totals = analyzeTotals(head, dataRows);
  // If we stripped a summary row but analyzeTotals refused (can't honestly total),
  // keep the model's own total visible as a plain row rather than dropping it.
  const bodyRows = (!totals && summaryRows.length) ? dataRows.concat(summaryRows) : dataRows;

  const tableEl = (
    <div className={`wiz-table-wrap${wide ? " wiz-table-wrap--wide" : ""}`}>
      <table className={`wiz-table${wide ? " wiz-table--wide" : ""}`}>
        <thead><tr>{head.map((c, i) => <th key={i}>{inline(c)}</th>)}</tr></thead>
        <tbody>{bodyRows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci}>{inline(c)}</td>)}</tr>)}</tbody>
        {totals && (
          <tfoot>
            <tr className="wiz-table__total">
              {head.map((_, ci) => (
                <td key={ci} title={totals.cells[ci] && totals.cells[ci].why ? totals.cells[ci].why : undefined}>
                  {ci === totals.labelCol ? totals.label : (totals.cells[ci] || {}).text || ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      {totals && totals.note && <div className="wiz-table__totalnote">{totals.note}</div>}
    </div>
  );

  if (!analysis.chartable) return tableEl;

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("wiz_tableview", v); } catch (_) {}
  };

  return (
    <div className="wiz-tablecard">
      <div className="wiz-tablecard__bar">
        <div className="wiz-seg">
          <button type="button" className={view === "table" ? "is-on" : ""} onClick={() => setViewPersist("table")}>Table</button>
          <button type="button" className={view === "chart" ? "is-on" : ""} onClick={() => setViewPersist("chart")}>Chart</button>
        </div>
        {view === "chart" && (
          <div className="wiz-seg wiz-seg--type">
            <button type="button" className={ctype === "line" ? "is-on" : ""} onClick={() => setCtype("line")} aria-label="Line chart">Line</button>
            <button type="button" className={ctype === "column" ? "is-on" : ""} onClick={() => setCtype("column")} aria-label="Bar chart">Bars</button>
          </div>
        )}
      </div>
      {view === "chart" ? (
        <div className="wiz-chart">
          <HighchartsReact highcharts={Highcharts} options={buildChartOptions(analysis, ctype, theme)} />
        </div>
      ) : tableEl}
    </div>
  );
}

export function renderContent(text, theme = "light") {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let bullets = [];
  let table = [];

  const flushBullets = (k) => {
    if (bullets.length) {
      blocks.push(<ul key={"ul" + k}>{bullets.map((b, i) => <li key={i}>{inline(b)}</li>)}</ul>);
      bullets = [];
    }
  };
  const flushTable = (k) => {
    if (!table.length) return;
    const rows = table.map(splitTableRow);
    const [head, ...rest] = rows;
    table = [];
    // A model "Period totals" line that happens to contain pipes parses as a
    // one-row table whose HEADER is the summary — not a real table. Drop it; the
    // UI's own footer is the single source of truth. (ChartableTable also drops
    // summary rows from a real table's body.)
    if (isSummaryRow(head)) return;
    blocks.push(<ChartableTable key={"tw" + k} head={head} rows={rest} theme={theme} />);
  };

  lines.forEach((line, idx) => {
    const t = line.trim();
    const isSep = /^[-\s|:]+$/.test(t) && t.includes("-");
    const isRow = t.includes("|") && t.split("|").length >= 2;

    if (isRow && !isSep) { flushBullets(idx); table.push(t); return; }
    if (isSep && table.length) return; // skip the ---|--- separator row

    flushTable(idx);
    if (t.startsWith("## ")) { flushBullets(idx); blocks.push(<h4 className="wiz-h" key={"h" + idx}>{t.slice(3)}</h4>); return; }
    if (t.startsWith("# ")) { flushBullets(idx); blocks.push(<h4 className="wiz-h" key={"h" + idx}>{t.slice(2)}</h4>); return; }
    if (t.startsWith("- ") || t.startsWith("• ")) { if (!isSummaryLine(t.slice(2))) bullets.push(t.slice(2)); return; }
    flushBullets(idx);
    if (t && !isSummaryLine(t)) blocks.push(<div key={"p" + idx}>{inline(t)}</div>);
  });
  flushTable("end");
  flushBullets("end");
  return blocks;
}

/* --- markdown -> HTML (for rich clipboard copy; mirrors renderContent) --- */
function escapeHtml(s) {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function inlineHtml(str) {
  return str.split(/(\*\*[^*]+\*\*)/g)
    .map((p) => (p.startsWith("**") && p.endsWith("**") ? `<strong>${escapeHtml(p.slice(2, -2))}</strong>` : escapeHtml(p)))
    .join("");
}
export function contentToHtml(text) {
  if (!text) return "";
  const lines = text.split("\n");
  const out = [];
  let bullets = [];
  let table = [];
  const flushBullets = () => {
    if (bullets.length) {
      out.push("<ul>" + bullets.map((b) => `<li>${inlineHtml(b)}</li>`).join("") + "</ul>");
      bullets = [];
    }
  };
  const flushTable = () => {
    if (!table.length) return;
    const rows = table.map(splitTableRow);
    const [head, ...bodyAll] = rows;
    table = [];
    // A summary line that parsed as a one-row table (its header IS the summary) is
    // dropped whole, mirroring the on-screen path.
    if (isSummaryRow(head)) return;
    // Drop model-authored summary rows ("Period Total"…) from the body and the
    // total, exactly as the on-screen table does, so the export can't double-count.
    const dataBody = bodyAll.filter((r) => !isSummaryRow(r));
    const summaryBody = bodyAll.filter((r) => isSummaryRow(r));
    // Same footer the screen shows — an exported thread that silently drops the
    // totals row would have people reconciling two different documents.
    const totals = analyzeTotals(head, dataBody);
    const body = (!totals && summaryBody.length) ? dataBody.concat(summaryBody) : dataBody;
    let t = '<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;">';
    t += "<thead><tr>" + head.map((c) => `<th style="text-align:left;">${inlineHtml(c)}</th>`).join("") + "</tr></thead>";
    t += "<tbody>" + body.map((r) => "<tr>" + r.map((c) => `<td>${inlineHtml(c)}</td>`).join("") + "</tr>").join("") + "</tbody>";
    if (totals) {
      t += "<tfoot><tr>" + head.map((_, ci) =>
        `<td style="font-weight:600;border-top:2px solid #333;">${escapeHtml(
          ci === totals.labelCol ? totals.label : (totals.cells[ci] || {}).text || ""
        )}</td>`).join("") + "</tr></tfoot>";
    }
    out.push(t + "</table>");
    if (totals && totals.note) out.push(`<div style="font-size:10px;color:#666;">${escapeHtml(totals.note)}</div>`);
    table = [];
  };
  lines.forEach((line) => {
    const s = line.trim();
    const isSep = /^[-\s|:]+$/.test(s) && s.includes("-");
    const isRow = s.includes("|") && s.split("|").length >= 2;
    if (isRow && !isSep) { flushBullets(); table.push(s); return; }
    if (isSep && table.length) return;
    flushTable();
    if (s.startsWith("## ")) { flushBullets(); out.push(`<h4>${escapeHtml(s.slice(3))}</h4>`); return; }
    if (s.startsWith("# ")) { flushBullets(); out.push(`<h4>${escapeHtml(s.slice(2))}</h4>`); return; }
    if (s.startsWith("- ") || s.startsWith("• ")) { if (!isSummaryLine(s.slice(2))) bullets.push(s.slice(2)); return; }
    flushBullets();
    if (s && !isSummaryLine(s)) out.push(`<div>${inlineHtml(s)}</div>`);
  });
  flushTable();
  flushBullets();
  return out.join("");
}

/* --- typewriter: smooth out chunky stream arrivals ------------------------
   The backend sanitizer is deliberately LINE-atomic (it must see a whole line
   before it can rule out SQL/ID leaks), so deltas reach us in line-sized
   clumps. Rendering them raw looks like the answer "jumps". This drains the
   accumulated text at reading speed instead — pure presentation, the data
   still arrives event-by-event. Drain rate scales with backlog so a long
   table never lags the stream by more than ~a second. */
function makeTypewriter(emit) {
  let target = "";
  let shown = 0;
  let timer = null;
  const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
  const tick = () => {
    if (shown >= target.length) { stop(); return; }
    const backlog = target.length - shown;
    const step = Math.max(3, Math.ceil(backlog * 0.12));
    shown = Math.min(target.length, shown + step);
    emit(target.slice(0, shown));
  };
  return {
    push(text) {
      target += text || "";
      if (!timer) timer = setInterval(tick, 28);
    },
    /* stream finished: show everything now (feedback row, PDF, copy all read
       the final DOM — they must never see a half-drained answer) */
    snap() { stop(); shown = target.length; if (target) emit(target); },
    /* caller is replacing the content (error text, abort) — just stand down */
    abort() { stop(); },
  };
}

/* --- friendly date helpers for the history panel --- */
function shortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined,
    sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 172800) return "yesterday";
  if (s < 604800) return `${Math.round(s / 86400)}d ago`;
  return shortDate(dateStr);
}

function fullStamp(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return isNaN(d) ? "" : d.toLocaleString();
}

/* Message timestamp in the user's LOCAL time: "3:42 PM" today, "Jul 12, 3:42 PM" otherwise.
   Backend sends UTC ISO strings; `new Date()` + toLocale* renders them in the browser's zone. */
function msgTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? time : `${shortDate(dateStr)}, ${time}`;
}

// AL-71: switch-hotel confirmation for email deep links. Pulled out of LodgiqWizard
// into its own component — the deeply nested ternary chain below, inlined in the
// parent render, was tripping this repo's eslint-plugin-react-hooks into flagging
// every hook in LodgiqWizard as "called conditionally" (a CFG complexity limit in
// that plugin version, not a real violation). A separate function scope keeps the
// complexity from bleeding into the parent's hook analysis.
function DeepLinkDialog({ deepLinkAsk, ctx, theme, onAccept, onDecline, onDismiss }) {
  if (!deepLinkAsk) return null;
  return (
    <div className="wiz-confirm-overlay" role="dialog" aria-modal="true" aria-label="Switch hotel?">
      <div className="wiz-confirm" data-theme={theme}>
        <div className="wiz-confirm__title">
          {deepLinkAsk.closeFailed
            ? "Staying put"
            : deepLinkAsk.hasAccess
              ? `Switch to ${deepLinkAsk.name}?`
              : "This link is for another hotel"}
        </div>
        <div className="wiz-confirm__body">
          {deepLinkAsk.closeFailed
            ? `Okay — staying on ${ctx.propertyName || "your current hotel"}. You can close this tab now.`
            : deepLinkAsk.error
              ? deepLinkAsk.error
              : deepLinkAsk.hasAccess
                // Same dialog, two ways in: an email deep link, or a portfolio
                // answer handing off to the hotel that needs the decision. Saying
                // "your email question" to someone who just clicked a briefing
                // chip is confusing enough to make them cancel.
                ? (deepLinkAsk.source === "portfolio"
                  ? `${deepLinkAsk.name} is in ${deepLinkAsk.scopeName || "this portfolio"}, but you're ` +
                    `currently working on ${ctx.propertyName || "another hotel"}. Only one hotel can be ` +
                    `active at a time — switching reloads the workspace for ${deepLinkAsk.name}, and your ` +
                    `question loads there.`
                  : `Your email question is for ${deepLinkAsk.name}, but you're currently working on ` +
                    `${ctx.propertyName || "another hotel"}. Only one hotel can be active at a time — ` +
                    `switching reloads the workspace for ${deepLinkAsk.name}.`)
                : (deepLinkAsk.source === "portfolio"
                  ? `${deepLinkAsk.name} isn't available on your account.`
                  : "This email link points to a hotel that isn't in your account.")}
        </div>
        {!deepLinkAsk.closeFailed && (
          <div className="wiz-confirm__q">“{deepLinkAsk.q}”</div>
        )}
        <div className="wiz-confirm__actions">
          {deepLinkAsk.closeFailed ? (
            <button className="wiz-confirm__btn wiz-confirm__btn--primary" onClick={onDismiss}>Got it</button>
          ) : deepLinkAsk.error ? (
            <button className="wiz-confirm__btn wiz-confirm__btn--primary" onClick={onDismiss}>OK — I'll switch from the menu</button>
          ) : deepLinkAsk.hasAccess ? (
            <>
              {/* An email deep link opened its own tab, so declining closes it.
                  A portfolio hand-off happened inside the app — closing the tab
                  there would throw away the session the user is working in. */}
              <button className="wiz-confirm__btn" onClick={deepLinkAsk.source === "portfolio" ? onDismiss : onDecline}>
                {deepLinkAsk.source === "portfolio" ? "Stay here" : "No — close this tab"}
              </button>
              <button className="wiz-confirm__btn wiz-confirm__btn--primary" onClick={onAccept}>
                Switch &amp; ask &#8599;
              </button>
            </>
          ) : (
            <>
              <button className="wiz-confirm__btn" onClick={onDismiss}>
                Dismiss
              </button>
              <button className="wiz-confirm__btn wiz-confirm__btn--primary" onClick={onDecline}>
                Close this tab
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LodgiqWizard() {
  // "closed" | "mini" | "docked" | "max"
  // X on a full window with an active conversation first goes to "mini" (half-height,
  // chat + workspace both visible); X again returns to the launcher.
  // Remembered panel size (AL-91) — "closed" is a transient state, never stored,
  // so an unreadable/absent value falls back to the docked default.
  const readLastSize = () => {
    try {
      const v = localStorage.getItem("wiz_size");
      return v === "mini" || v === "max" ? v : "docked";
    } catch (_) { return "docked"; }
  };
  const [mode, setMode] = useState("closed");
  useEffect(() => {
    if (mode === "mini" || mode === "docked" || mode === "max") {
      try { localStorage.setItem("wiz_size", mode); } catch (_) {}
    }
  }, [mode]);

  // Has the user ever picked a size from the header control? Deliberately NOT
  // inferred from wiz_size, which every mode change writes — including the ones
  // the app makes on its own. Only a click on the segment control counts, and it
  // survives reloads so an auto-resize can't come back tomorrow.
  const sizeChosenRef = useRef(
    (() => { try { return localStorage.getItem("wiz_size_locked") === "1"; } catch (_) { return false; } })()
  );
  const chooseSize = (m) => {
    sizeChosenRef.current = true;
    try { localStorage.setItem("wiz_size_locked", "1"); } catch (_) {}
    setMode(m);
  };
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // User-resizable docked width (persisted). Min 380, max ~900 / 96vw.
  const initW = (() => {
    const v = Number((typeof localStorage !== "undefined" && localStorage.getItem("wiz_width")) || 0);
    return v >= 380 ? v : 540;
  })();
  const [width, setWidth] = useState(initW);
  const widthRef = useRef(initW);

  // A wide table (>6 columns) asked for more room. Give it the docked panel rather
  // than letting the type shrink — but ONLY for someone who has never touched the
  // size control.
  //
  // This used to escalate mini -> docked unconditionally, which meant picking
  // "Half" and then asking anything that returns a table wider than six columns
  // silently threw you back to docked — and, because mode is persisted in
  // wiz_size, overwrote the stored preference too. The button looked broken; it
  // was working and being undone a moment later.
  //
  // `wiz_size` can't tell us whether a size was CHOSEN, since every mode change
  // writes it. Hence a separate flag, set only by the three segment buttons.
  useEffect(() => {
    const onWide = () => {
      if (!sizeChosenRef.current) setMode((m) => (m === "mini" ? "docked" : m));
      try {
        if (!localStorage.getItem("wiz_width") && widthRef.current < 720) {
          widthRef.current = 720;
          setWidth(720);
        }
      } catch (_) {}
    };
    document.addEventListener("wizWideTable", onWide);
    return () => document.removeEventListener("wizWideTable", onWide);
  }, []);

  // User-resizable maximized window size (persisted).
  const initMax = (() => {
    try {
      const v = JSON.parse((typeof localStorage !== "undefined" && localStorage.getItem("wiz_maxsize")) || "null");
      if (v && v.w && v.h) return v;
    } catch (_) {}
    return null;
  })();
  const [maxSize, setMaxSize] = useState(initMax);
  const maxSizeRef = useRef(initMax);

  // Session / history state
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [status, setStatus] = useState(""); // live "Checking …" indicator during tool calls
  const [suggestions, setSuggestions] = useState([]); // follow-up question chips
  const [showSuggestions, setShowSuggestions] = useState(false); // Option B: collapsed by default
  const [toolHints, setToolHints] = useState([]); // deterministic "open this tool" chips
  const [showTools, setShowTools] = useState(false); // composer "+" toolbox menu
  const toolsRef = useRef(null);
  // Morning briefing (per-property daily digest — lazy, first open of the day)
  const [brief, setBrief] = useState(null);            // ready briefing JSON
  const [briefText, setBriefText] = useState("");      // narrative while streaming
  const [briefStatus, setBriefStatus] = useState("none"); // none|loading|streaming|ready|dismissed|failed
  const [briefPref, setBriefPref] = useState(true);
  // When the briefing occupies the empty state, the tool grid collapses behind
  // a "Start with a tool" button so the briefing keeps the focus.
  const [toolsIntroOpen, setToolsIntroOpen] = useState(false);
  // Feedback: which message id has the optional-comment box open, and its draft text.
  const [fbCommentFor, setFbCommentFor] = useState(null);
  const [fbCommentText, setFbCommentText] = useState("");
  const [fbSaving, setFbSaving] = useState(false);
  // THEME (AL-91): the main application has no dark mode, so a dark chat panel
  // inside a light app reads as broken. The toggle is removed and the panel is
  // pinned to light. The dark SCSS and the data-theme plumbing below are kept
  // deliberately — when the app gains a theme, this becomes a one-line change.
  // `wiz_theme` is no longer read, so anyone who had dark on comes back to light
  // instead of being stranded with no way to switch back.
  const theme = "light";
  const [copiedIdx, setCopiedIdx] = useState(null);        // which message was just copied
  const [showResume, setShowResume] = useState(false);     // "resume or new" landing
  const [loadedHistory, setLoadedHistory] = useState(false); // current thread came from history

  // Insight display preference menu (gear in the header) — where "Summarize this
  // view" appears: this chat (default) or the inline panel over the view.
  const [showPrefMenu, setShowPrefMenu] = useState(false);
  const [msgMenuIdx, setMsgMenuIdx] = useState(null); // per-answer "…" menu (index or null)
  const msgMenuRef = useRef(null);
  const [insightPref, setInsightPref] = useState("chat");

  // Internal A/B/C compare mode — visible ONLY to AB testers (email allow-list on the server).
  const [abTester, setAbTester] = useState(false);
  const [abProfiles, setAbProfiles] = useState([]);        // [{key,label,vendor,engine}]
  // Portfolio context (AL-80). `portfolios` empty (or mode disabled server-side)
  // means no picker renders at all — property mode is untouched.
  const [portfolios, setPortfolios] = useState([]);
  const [activePortfolio, setActivePortfolio] = useState(null);   // {id,name,property_count}
  const [showCtxMenu, setShowCtxMenu] = useState(false);
  const [announcement, setAnnouncement] = useState(null);  // "new this week" chip {id,text} (AL-48)
  const [gallery, setGallery] = useState([]);               // AL-47: [{theme, examples[]}] per property
  const [showGallery, setShowGallery] = useState(false);    // "?" re-open of the gallery mid-session
  const [compareMode, setCompareMode] = useState(false);
  const [compareSel, setCompareSel] = useState([]);        // selected profile keys (max 4)
  const [showComparePicker, setShowComparePicker] = useState(false);
  const sessionRef = useRef(null);   // avoids stale closure inside stream callbacks
  const abortRef = useRef(null);

  // The RM app's dropdowns (market picker, scope, segments — ~30 of them) are MUI
  // <Select> menus, and an open MUI menu is a MODAL: it traps focus and pulls it
  // back whenever focus leaves. So clicking into the chat composer while one is
  // open sent every keystroke to the dropdown's own "Type to search" box, and the
  // composer looked dead until the page was reloaded.
  //
  // We are not going to add disableEnforceFocus to thirty components we don't
  // own. Instead: when a press lands inside the chat, close whatever overlay is
  // open first. Escape is the documented way out of a MUI menu, and the search
  // field in marketPicker.js deliberately lets Escape through while swallowing
  // every other key.
  //
  // Dispatched on document.activeElement rather than document: the focus trap
  // guarantees the active element is INSIDE the menu, so the event bubbles up to
  // the modal root that listens for it. Dispatching on document would travel the
  // wrong way and be ignored.
  const dismissAppOverlays = () => {
    // Four menus in the app render with keepMounted, so their modal root is in
    // the DOM even when shut. Presence is not openness — test that the overlay is
    // actually rendered, and prefer the one that currently holds focus, since
    // "focus sits inside a MUI modal" IS the condition that breaks our composer.
    const roots = Array.from(
      document.querySelectorAll(".MuiModal-root, .MuiPopover-root, .MuiMenu-root")
    ).filter(
      (el) =>
        el.getAttribute("aria-hidden") !== "true" &&
        el.getClientRects().length > 0 &&
        window.getComputedStyle(el).visibility !== "hidden"
    );
    if (!roots.length) return false;               // nothing open: do nothing at all
    const focused = roots.find((el) => el.contains(document.activeElement));
    const open = focused || roots[0];
    const target = (focused && document.activeElement) || open;
    try {
      target.dispatchEvent(new KeyboardEvent("keydown", {
        key: "Escape", code: "Escape", keyCode: 27, which: 27, bubbles: true, cancelable: true,
      }));
    } catch (_) { /* older browsers: the click-away below is the fallback */ }
    return true;
  };

  // Fired capture-phase on the panel so it runs BEFORE the click does anything.
  const onPanelMouseDownCapture = (e) => {
    if (!dismissAppOverlays()) return;
    // MUI releases the trap on its next render, so re-assert focus after it.
    const el = e.target;
    setTimeout(() => {
      try {
        // Belt and braces: anything that ignored Escape gets the click-away it
        // would have got if the user had clicked the page instead of the chat.
        if (dismissAppOverlays()) {
          document.querySelectorAll(".MuiBackdrop-root").forEach((b) => {
            if (b.getClientRects().length) {
              ["mousedown", "mouseup", "click"].forEach((t) =>
                b.dispatchEvent(new MouseEvent(t, { bubbles: true, cancelable: true }))
              );
            }
          });
        }
        if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
        else if (taRef.current) taRef.current.focus();
      } catch (_) {}
    }, 0);
  };

  // ONE way to abort, and it clears `loading` itself.
  //
  // Why this exists: `loading` is what gates send() — with it stuck true you can
  // still type, but Enter and the send button silently do nothing, and only a
  // page reload frees the composer. Clearing it was previously the job of each
  // stream caller's onAbort handler, and two of the three callers never had one
  // ("Summarize this view" and A/B compare), so aborting either — by closing the
  // panel, switching property or switching scope mid-stream — wedged the box.
  //
  // The abort site knows it just aborted; it should not depend on every present
  // and future caller remembering a handler. The onAbort handlers still exist and
  // still own what the MESSAGE should say; this owns the flag.
  const abortActiveStream = () => {
    try { if (abortRef.current) abortRef.current.abort(); } catch (_) {}
    setLoading(false);
    setStatus("");
  };
  const prevPropRef = useRef(null);

  // Brief highlight over the workspace after deep-linking from a source chip.
  const [flash, setFlash] = useState(null); // {top,left,width,height} | null
  const flashTimerRef = useRef(null);

  // Draggable launcher. Position persists for THIS login in sessionStorage
  // (cleared when the browser/tab closes); the stored token snapshot makes a
  // logout/re-login reset it to the default corner too.
  const [launcherPos, setLauncherPos] = useState(() => {
    try {
      const raw = sessionStorage.getItem("wiz_launcher_pos");
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (v && typeof v.x === "number" && typeof v.y === "number" && v.t === getFromSession("token")) {
        return { x: v.x, y: v.y };
      }
      sessionStorage.removeItem("wiz_launcher_pos"); // different login -> default corner
    } catch (_) {}
    return null;
  });
  const launcherDragRef = useRef({ moved: false });

  // Mini mode is a floater: drag it by the header to uncover any chart/table
  // beneath. The offset applies to mini ONLY — leaving mini (docked/max/closed)
  // discards it, so the window always returns to its normal position.
  const [miniPos, setMiniPos] = useState(null); // {x,y} | null = default corner
  // Mini height (px) — null = the default 46vh. Resizable + remembered.
  const [miniHeight, setMiniHeight] = useState(() => {
    try { const v = parseInt(localStorage.getItem("wiz_mini_h"), 10); return v > 0 ? v : null; } catch (_) { return null; }
  });

  const onMiniHeaderDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button")) return; // header buttons keep working
    const win = e.currentTarget.closest(".wiz-window");
    if (!win) return;
    const rect = win.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const move = (ev) => {
      const x = Math.max(4, Math.min((window.innerWidth || 1200) - rect.width - 4, ev.clientX - offX));
      const y = Math.max(4, Math.min((window.innerHeight || 800) - 60, ev.clientY - offY));
      setMiniPos({ x, y });
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };

  // Any transition out of mini resets the floater to its default corner.
  useEffect(() => {
    if (mode !== "mini" && miniPos) setMiniPos(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Close the composer "+" toolbox on any outside click.
  useEffect(() => {
    if (!showTools) return undefined;
    const onDown = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showTools]);

  // Per-answer overflow menu ("…"). Holds the index of the message whose menu is
  // open, or null. One menu at a time; closes on outside click or Escape.
  // This is the CONTAINER the queued per-answer actions land in — in-app share
  // (AL-10), email/Slack share (AL-22), pin as decision (AL-23), read aloud
  // (AL-98), task from answer (AL-99). Four more buttons would wreck this row.
  useEffect(() => {
    if (msgMenuIdx === null) return undefined;
    const onDown = (e) => {
      if (msgMenuRef.current && !msgMenuRef.current.contains(e.target)) setMsgMenuIdx(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setMsgMenuIdx(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [msgMenuIdx]);

  const clampPos = (x, y, w = 150, h = 40) => ({
    x: Math.max(8, Math.min((window.innerWidth || 1200) - w - 8, x)),
    y: Math.max(8, Math.min((window.innerHeight || 800) - h - 8, y)),
  });

  const onLauncherMouseDown = (e) => {
    if (e.button !== 0) return;
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const startX = e.clientX, startY = e.clientY;
    launcherDragRef.current = { moved: false };

    const move = (ev) => {
      if (!launcherDragRef.current.moved &&
          Math.abs(ev.clientX - startX) < 5 && Math.abs(ev.clientY - startY) < 5) return;
      launcherDragRef.current.moved = true;
      setLauncherPos(clampPos(ev.clientX - offX, ev.clientY - offY, rect.width, rect.height));
    };
    const up = (ev) => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      if (launcherDragRef.current.moved) {
        const p = clampPos(ev.clientX - offX, ev.clientY - offY, rect.width, rect.height);
        try {
          sessionStorage.setItem("wiz_launcher_pos", JSON.stringify({ ...p, t: getFromSession("token") }));
        } catch (_) {}
      }
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.body.style.userSelect = "none";
  };

  const bodyRef = useRef(null);
  const taRef = useRef(null);
  const voiceRef = useRef(null);       // AL-59: lets send() stop dictation (declared later)
  const [deepLinkNote, setDeepLinkNote] = useState("");   // AL-71: email deep-link notice

  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  // Product-variant gate, so a source chip never lands the user on a lock screen.
  const { hasFeature } = useProductVariant();
  const state = useSelector((s) => s) || {};
  const app_state = state?.app?.current_state?.app_state || {};
  const user = state?.auth?.user || {};
  const ctx = {
    propertyName: app_state.property_name,
    propertyId: app_state.property_id,
    userName: user.fullname,
  };

  // Per-user access gate (admin-toggleable). Hidden entirely unless explicitly disabled.
  // NOTE: written with ternaries, not `??` — this repo's eslint-plugin-react-hooks
  // treats a top-level `??` as a conditional code path and then (wrongly) flags
  // every hook below this line as "called conditionally".
  const _askUser = state?.auth?.user?.ask_lodgiq_enabled;
  const _askProfile = state?.auth?.profile?.ask_lodgiq_enabled;
  const askEnabled = _askUser != null ? _askUser : _askProfile != null ? _askProfile : true;

  const scrollToBottom = () => {
    // Two rAFs so it runs after the new messages (tables/long answers) have laid out.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      });
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, mode]);

  // Handoff from the workspace AI-insight panel: open the chat in mini mode on
  // the insight's session so the user can ask follow-ups with full context.
  const openSessionRef = useRef(null);
  const runInsightRef = useRef(null);
  useEffect(() => {
    const onOpenChat = (e) => {
      const detail = (e && e.detail) || {};
      setMode("mini");
      if (detail.view && runInsightRef.current) {
        // insight_display = "chat": run "summarize this view" right here.
        runInsightRef.current(detail.view, detail.intent || "summarize");
      } else if (detail.sessionId && openSessionRef.current) {
        openSessionRef.current(detail.sessionId);
      }
    };
    window.addEventListener("lq:open-chat", onOpenChat);
    return () => window.removeEventListener("lq:open-chat", onOpenChat);
  }, []);

  // Resolve the insight-display preference (device override -> login payload -> chat).
  useEffect(() => {
    setInsightPref(getInsightDisplay(user?.insight_display));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.insight_display]);

  const chooseInsightPref = (opt) => {
    setInsightPref(opt);
    setShowPrefMenu(false);
    saveInsightDisplay(opt);
  };

  // ---- Morning briefing: fetch (or lazily generate) on property change ----
  useEffect(() => {
    setBriefPref(getBriefingPref(user?.morning_briefing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.morning_briefing]);

  // AL-94: the key carries the SCOPE. Dismissing this hotel's briefing must not
  // also dismiss the portfolio's — they are different briefings on the same day.
  const briefDismissKey = (pid, date, pfId) =>
    (pfId ? `lq_brief_dismiss:pf:${pfId}:${date}` : `lq_brief_dismiss:${pid}:${date}`);
  const todayIso = () => new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const pid = ctx.propertyId;
    const pfId = activePortfolio ? activePortfolio.id : null;
    setBrief(null);
    setBriefText("");
    setBriefStatus("none");
    setToolsIntroOpen(false);
    if (!pid || !askEnabled || !briefPref) return undefined;

    let alive = true;
    let timer = null;
    const controller = new AbortController();

    const arrived = (b) => {
      if (!alive || !b) return;
      try {
        if (localStorage.getItem(briefDismissKey(pid, b.date, pfId))) { setBriefStatus("dismissed"); return; }
      } catch (_) {}
      setBrief(b);
      setBriefStatus("ready");
    };

    const generate = () => {
      if (!alive) return;
      setBriefStatus("streaming");
      streamBriefing(pid, {
        portfolioId: pfId,
        signal: controller.signal,
        onDelta: (d) => { if (alive) setBriefText((t) => t + d); },
        onDone: (b, st) => {
          if (!alive) return;
          if (b) arrived(b);
          else if (st === "generating") poll(12);   // someone else is generating
          else setBriefStatus("failed");            // quiet failure — card hides
        },
      });
    };

    const poll = (left) => {
      if (!alive || left <= 0) { setBriefStatus("failed"); return; }
      timer = setTimeout(() => {
        getBriefing(pid, pfId).then((res) => {
          if (!alive) return;
          if (res.status === "ready") arrived(res.briefing);
          else if (res.status === "generating") poll(left - 1);
          else setBriefStatus("failed");
        });
      }, 3000);
    };

    setBriefStatus("loading");
    getBriefing(pid, pfId).then((res) => {
      if (!alive) return;
      if (res.status === "ready") arrived(res.briefing);
      else if (res.status === "generating") poll(12);
      else if (res.status === "none" || res.status === "failed") generate();
      else setBriefStatus("none");   // disabled etc.
    });

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.propertyId, askEnabled, briefPref, activePortfolio && activePortfolio.id]);

  const dismissBriefing = () => {
    const d = (brief && brief.date) || todayIso();
    const pfId = activePortfolio ? activePortfolio.id : null;
    try { localStorage.setItem(briefDismissKey(ctx.propertyId, d, pfId), "1"); } catch (_) {}
    setBriefStatus("dismissed");
  };

  const briefingFeedback = (fb) => {
    if (!brief) return;
    setBrief((b) => ({ ...b, feedback: fb }));
    sendBriefingFeedback(ctx.propertyId, brief.id, fb);
  };

  const briefingFollowUp = () => {
    setInput("Following up on this morning's briefing: ");
    if (taRef.current) taRef.current.focus();
  };

  // AL-94: the portfolio card's whole job is to hand the user off to the hotel
  // where the decision is made. Leave portfolio scope, then load the question —
  // asking a per-hotel question while still in portfolio scope is exactly the
  // "which hotel?" refusal the routing rules produce.
  const portfolioBriefFollowUp = () => {
    setInput(`Rank my hotels in ${activePortfolio ? activePortfolio.name : "this portfolio"} by occupancy`);
    if (taRef.current) taRef.current.focus();
  };

  const openBriefingHotel = (link) => {
    if (!link) return;
    const target = Number(link.property_id || 0);
    const samePid = !target || String(target) === String(ctx.propertyId);
    try {
      window.analyze && window.analyze.track("askq_portfolio_brief_handoff", {
        same_property: samePid, has_question: !!link.question,
      });
    } catch (_) {}

    if (samePid) {
      // The hotel is already active, so this IS just a scope change: leave the
      // portfolio and load the question here.
      setActivePortfolio(null);
      setInput(link.question || "");
      if (taRef.current) taRef.current.focus();
      return;
    }
    // A different hotel needs a workspace switch, which is the user's call. The
    // portfolio stays selected until they accept — dropping it now would cost
    // them their scope for a switch they might decline.
    // Different hotel: reuse the AL-71 stash so the question survives the
    // property switch (a big dispatch + reload) and prefills on the other side.
    try {
      // `src` tags where the stash came from. The consumer on the other side is
      // AL-71's email handler, and without this it greets a portfolio hand-off
      // with "From your email".
      sessionStorage.setItem("lqx_askq", JSON.stringify({
        q: link.question || "", pid: String(target), ts: Date.now(), src: "portfolio",
      }));
    } catch (_) {}
    const details = state?.auth?.profile?.property_details || [];
    const found = details.find((p) => String(p.id) === String(target));
    setDeepLinkAsk({
      q: link.question || "", pid: String(target),
      name: (found && (found.name || found.label)) || link.label || "that hotel",
      hasAccess: !!found,
      // Tells the dialog it arrived from a portfolio hand-off, not an email.
      source: "portfolio",
      scopeName: activePortfolio ? activePortfolio.name : "",
    });
  };

  const chooseBriefPref = (enabled) => {
    setBriefPref(enabled);
    setShowPrefMenu(false);
    saveBriefingPref(enabled);
    if (!enabled) setBriefStatus("none");
  };

  const briefVisible = (briefStatus === "streaming" || briefStatus === "ready") && !compareMode;

  // Tools the CURRENT scope can actually answer with. Empty in portfolio mode
  // today — every tool is per-hotel — so the tool affordances hide entirely
  // rather than presenting an empty tray.
  const scopedTools = TOOL_MENU.filter(
    (t) => t.scope === (activePortfolio ? "portfolio" : "property")
  );

  // AL-94: did the last answer ask WHICH hotel? Narrow on purpose — it must not
  // fire on an answer that merely mentions hotels.
  const lastMsg = messages.length ? messages[messages.length - 1] : null;
  const whichHotelChips = !!(
    activePortfolio
    && (activePortfolio.hotels || []).length > 0
    && lastMsg && lastMsg.role === "assistant" && !lastMsg.error
    && /which (hotel|property|one)\b[^.?!]*\?/i.test(lastMsg.content || "")
  );

  // Answering it: leave portfolio scope for that hotel, then re-ask the question
  // that triggered the refusal so the user never retypes it.
  const answerWhichHotel = (hotelName) => {
    const prevUser = [...messages].reverse().find((m) => m.role === "user");
    const q = prevUser ? prevUser.content : "";
    setActivePortfolio(null);
    try {
      window.analyze && window.analyze.track("askq_which_hotel_answered", { had_question: !!q });
    } catch (_) {}
    // The hotel is named in the question rather than switched to silently: the
    // ambient property may be a different one, and an answer about the wrong
    // hotel is worse than one extra clarifying step.
    send(q ? `${q} — for ${hotelName}` : `Tell me about ${hotelName}`, "which_hotel");
  };
  const briefBadge = briefStatus === "ready" && brief && brief.badge;

  // One-time: which portfolios can this user ask across? (AL-80) Per user, so it
  // isn't refetched on property switch. An empty list = no picker.
  useEffect(() => {
    let alive = true;
    getPortfolios()
      .then((cfg) => {
        if (!alive) return;
        setPortfolios(cfg.enabled ? (cfg.portfolios || []) : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // One-time: am I an internal AB tester? If so, load the pickable model profiles.
  // Also carries the "new this week" announcement (AL-48) for ALL users.
  useEffect(() => {
    let alive = true;
    getAbConfig()
      .then((cfg) => {
        if (!alive) return;
        const ann = cfg.announcement;
        if (ann && ann.id && ann.text) {
          try {
            if (localStorage.getItem("wiz_ann_" + ann.id) !== "1") setAnnouncement(ann);
          } catch (_) { setAnnouncement(ann); }
        }
        if (!cfg.ab_tester) return;
        setAbTester(true);
        setAbProfiles(cfg.profiles || []);
        setCompareSel((cfg.profiles || []).slice(0, 2).map((p) => p.key));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismissAnnouncement = () => {
    if (announcement) { try { localStorage.setItem("wiz_ann_" + announcement.id, "1"); } catch (_) {} }
    setAnnouncement(null);
  };

  // Re-scope the whole chat when the selected property changes (or first mount).
  useEffect(() => {
    const pid = ctx.propertyId;
    if (prevPropRef.current === pid) return;
    prevPropRef.current = pid;
    abortActiveStream();
    sessionRef.current = null;
    setSessionId(null);
    setMessages([]);
    setSuggestions([]);
    setToolHints([]);
    setStatus("");
    setLoading(false);
    setLoadedHistory(false);
    setShowHistory(false);
    if (!pid) {
      setSessions([]);
      setShowResume(false);
      setGallery([]);
    }
    setShowGallery(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.propertyId]);

  // History follows the SCOPE too. Mixed together, a portfolio thread sat in a
  // hotel's history looking like it was about that hotel — and in portfolio mode
  // the list was every member hotel's threads at once, which stops being usable
  // long before 100 hotels.
  const prevScopeRef = useRef(undefined);
  useEffect(() => {
    const pid = ctx.propertyId;
    const scope = activePortfolio ? activePortfolio.id : "";
    // Changing scope starts a NEW conversation. A session is stamped with the
    // scope it was asked in, so continuing a hotel thread under a portfolio (or
    // the reverse) is refused server-side — and rightly: the earlier answers
    // were about something else. Skipped on first mount.
    if (prevScopeRef.current !== undefined && prevScopeRef.current !== scope) {
      abortActiveStream();
      sessionRef.current = null;
      setSessionId(null);
      setMessages([]);
      setSuggestions([]);
      setToolHints([]);
      setStatus("");
      setLoading(false);
      setLoadedHistory(false);
      // The tool tray unmounts in portfolio mode; without this it would spring
      // open by itself on the way back.
      setShowTools(false);
      // Same for A/B compare: its control is hidden in portfolio mode, so leaving
      // the MODE on would strand the user in a compare view with no way out.
      if (scope) { setCompareMode(false); setShowComparePicker(false); }
    }
    prevScopeRef.current = scope;
    if (!pid) return undefined;
    let alive = true;
    listSessions(pid, scope || null)
      .then((list) => {
        if (!alive) return;
        setSessions(list || []);
        setShowResume((list || []).length > 0);
      })
      .catch(() => {
        if (!alive) return;
        setSessions([]);
        setShowResume(false);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.propertyId, activePortfolio && activePortfolio.id]);

  // AL-47 + AL-82: the example gallery follows the SCOPE, not just the property —
  // in portfolio mode the questions are cross-hotel ones ("rank my hotels"), and
  // offering "should I raise Saturday's rate?" there would be answered with
  // "which hotel?". Kept as its own effect so switching scope refreshes the
  // examples without resetting the conversation the way a property switch does.
  useEffect(() => {
    const pid = ctx.propertyId;
    if (!pid) return;
    getCapabilities(pid, !!activePortfolio)
      .then((t) => setGallery(t || []))
      .catch(() => setGallery([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.propertyId, activePortfolio && activePortfolio.id]);

  // ---- AL-71: consume the engagement-email deep link ----------------------
  // index.js stashed {q, pid} in sessionStorage before any login redirect could
  // eat the query string. The app works on ONE current property (web + mobile),
  // so: link matches the current hotel -> open + PREFILL (never auto-send);
  // link is for a DIFFERENT hotel -> ask first (Sougata's rule) — yes switches
  // through the Header's own machinery, no closes the tab. The stash survives
  // the switch: when ctx.propertyId lands on the target, this effect re-runs
  // and takes the matching branch.
  const [deepLinkAsk, setDeepLinkAsk] = useState(null);
  const switchWatchRef = useRef(null);        // watchdog: did the switch actually land?
  const propIdRef = useRef(ctx.propertyId);
  propIdRef.current = ctx.propertyId;
  useEffect(() => {
    if (!askEnabled || !ctx.propertyId) return;
    let stash = null;
    try {
      stash = JSON.parse(sessionStorage.getItem("lqx_askq") || "null");
    } catch (_) { stash = null; }
    if (!stash || !stash.q) return;
    if (Date.now() - (stash.ts || 0) > 30 * 60 * 1000) {           // stale (>30min)
      try { sessionStorage.removeItem("lqx_askq"); } catch (_) {}
      return;
    }

    const pid = String(stash.pid || "");
    const samePid = !pid || String(ctx.propertyId) === pid;
    const fromPortfolio = stash.src === "portfolio";
    try {
      if (window.analyze && window.analyze.track) {
        window.analyze.track(fromPortfolio ? "askq_portfolio_handoff_open" : "askq_email_open", {
          property_id: ctx.propertyId, intended_pid: pid || null,
          matched: samePid, chars: stash.q.length,
        });
      }
    } catch (_) {}

    if (samePid) {
      try { sessionStorage.removeItem("lqx_askq"); } catch (_) {}  // consume once
      if (switchWatchRef.current) { clearTimeout(switchWatchRef.current); switchWatchRef.current = null; }
      setDeepLinkAsk(null);
      setMode("docked");
      setInput(stash.q);
      setDeepLinkNote(fromPortfolio
        ? `Carried over from your portfolio view — this asks about ${ctx.propertyName || "this hotel"}. Review and press send.`
        : "From your email — review the question and press send.");
      // focus after the window animates in
      setTimeout(() => {
        const ta = taRef.current;
        if (ta) {
          ta.focus();
          ta.style.height = "auto";
          ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
        }
      }, 350);
      return;
    }

    // different hotel: keep the stash (it powers the post-switch prefill) and ask
    const details = state?.auth?.profile?.property_details || [];
    const target = details.find((p) => String(p.id) === pid);
    setDeepLinkAsk({
      q: stash.q, pid,
      name: (target && (target.name || target.label)) || `hotel #${pid}`,
      hasAccess: !!target,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askEnabled, ctx.propertyId]);

  const acceptDeepLinkSwitch = () => {
    if (!deepLinkAsk) return;
    // Accepting a portfolio hand-off means leaving portfolio scope: the question
    // waiting on the other side is about that one hotel.
    if (deepLinkAsk.source === "portfolio") setActivePortfolio(null);
    try {   // refresh the stash TTL — the workspace reload takes a while
      const s = JSON.parse(sessionStorage.getItem("lqx_askq") || "null");
      if (s) { s.ts = Date.now(); sessionStorage.setItem("lqx_askq", JSON.stringify(s)); }
    } catch (_) {}
    const bridged = typeof window.__lqSwitchProperty === "function";
    const ok = bridged && window.__lqSwitchProperty(Number(deepLinkAsk.pid));
    voiceTrack("askq_email_switch", { accepted: true, ok, bridged, pid: deepLinkAsk.pid });
    if (!bridged) console.warn("AL-71 | __lqSwitchProperty missing — is the Header mounted?");
    if (!ok) {
      setDeepLinkAsk((d) => (d ? {
        ...d,
        error: `Couldn't switch automatically — pick ${d.name} from the hotel menu at ` +
               "the top, and your question will load there.",
      } : d));
      return;
    }
    // Watchdog: the Header's switch is a fetch + big dispatch and can fail
    // silently (its own .catch just logs). If the property hasn't changed in
    // 20s, resurface the dialog with the manual-switch fallback instead of
    // leaving the user wondering why nothing happened.
    const asked = deepLinkAsk;
    if (switchWatchRef.current) clearTimeout(switchWatchRef.current);
    switchWatchRef.current = setTimeout(() => {
      switchWatchRef.current = null;
      if (String(propIdRef.current) !== String(asked.pid)) {
        console.warn("AL-71 | switch watchdog: still on", propIdRef.current,
          "after 20s — check for 'error in handleClosePropertiesMenu' in the console");
        setDeepLinkAsk({
          ...asked,
          error: `The switch to ${asked.name} didn't complete — pick it from the hotel ` +
                 "menu at the top, and your question will load there.",
        });
      }
    }, 20000);
    setDeepLinkAsk(null);   // ctx.propertyId will change; the effect prefills then
  };

  const declineDeepLinkSwitch = () => {
    try { sessionStorage.removeItem("lqx_askq"); } catch (_) {}
    voiceTrack("askq_email_switch", { accepted: false, pid: deepLinkAsk && deepLinkAsk.pid });
    window.close();   // email links open in their own tab — usually closable
    // browser refused (tab not script-closable): say so instead of going silent
    setTimeout(() => setDeepLinkAsk((d) => (d ? { ...d, closeFailed: true } : d)), 250);
  };

  const dismissDeepLinkAsk = () => {
    try { sessionStorage.removeItem("lqx_askq"); } catch (_) {}
    setDeepLinkAsk(null);
  };

  // EVERY session fetch goes through here. Three call sites each passed the scope
  // separately and two of them forgot, so reopening the panel in portfolio mode
  // silently refilled the list with the hotel's conversations — the resume card
  // then offered "Open the revenue calculator" over a portfolio. One helper, one
  // place to get it wrong.
  const fetchScopedSessions = () => {
    if (!ctx.propertyId) return Promise.resolve([]);
    return listSessions(ctx.propertyId, activePortfolio ? activePortfolio.id : null)
      .then((list) => list || [])
      .catch(() => []);
  };

  const open = () => {
    // Reopen at the size the user last chose (AL-91). Deep links and workspace
    // hand-offs still set their own mode explicitly after this.
    setMode(readLastSize());
    if (ctx.propertyId) {
      fetchScopedSessions().then((list) => {
        setSessions(list);
        if (!messages.length) setShowResume(list.length > 0);
      });
    }
  };

  const closeWindow = () => {
    // X ALWAYS closes (AL-91). It used to mean "minimize" from a full window and
    // "close" from mini — one control, two behaviours, switching on state the
    // user couldn't see. "Go smaller" is now the half button in the size segment.
    // Closing is non-destructive: the session is persisted server-side and the
    // launcher offers to resume it.
    abortActiveStream();
    setShowHistory(false);
    setMode("closed");
  };

  const loadSessions = () => {
    fetchScopedSessions().then(setSessions);
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadSessions();
  };

  const newChat = () => {
    abortActiveStream();
    sessionRef.current = null;
    setSessionId(null);
    setMessages([]);
    setSuggestions([]);
    setToolHints([]);
    setShowHistory(false);
    setShowResume(false);
    setLoadedHistory(false);
  };

  const openSession = (sid) => {
    if (!ctx.propertyId) return;
    // AL-94: scope is a property of the SESSION (ChatSession.portfolio_id), so a
    // reopened conversation keeps its scope marker. Without this a portfolio
    // thread reads as if it had been about the current hotel all along.
    const sess = (sessions || []).find((s) => String(s.id) === String(sid));
    const sessScope = (sess && sess.portfolio_name) || "";
    getSession(ctx.propertyId, sid)
      .then((msgs) => {
        setMessages(msgs.map((m) => ({
          pfScope: sessScope,   // scope belongs to the whole conversation
          id: m.id,
          role: m.role,
          content: m.content,
          context_as_of: m.context_as_of,
          created_at: m.created_at,
          feedback: m.feedback || "",
          feedback_comment: m.feedback_comment || "",
          sources: m.sources || null,
          engine: m.engine || "",
          widgets: Array.isArray(m.widgets) ? m.widgets : null,
        })));
        setSuggestions([]);
        setToolHints([]);
        sessionRef.current = sid;
        setSessionId(sid);
        setShowHistory(false);
        setShowResume(false);
        setLoadedHistory(true);
        scrollToBottom();
      })
      .catch(() => {});
  };

  openSessionRef.current = openSession; // keep the insight-handoff listener fresh

  // "Summarize this view" inside the chat (insight_display = "chat", the default).
  // Mirrors send(): user bubble + streamed assistant bubble, but the payload is the
  // view descriptor — the backend builds the question and the view-scoped context.
  // Holds a "Summarize this view" fired while a portfolio was selected, until the
  // scope has dropped back to the hotel the view belongs to.
  const pendingInsightRef = useRef(null);

  const runViewInsight = (view, intent = "summarize") => {
    if (!ctx.propertyId || loading) return;
    // A dashboard view belongs to ONE hotel, so summarizing it is a per-hotel
    // action. In portfolio scope it used to try to continue the portfolio
    // conversation and was refused ("Session not found"). Drop to the hotel's
    // scope instead: the mode treatment visibly flips back, the thread resets,
    // and the insight runs where its numbers actually come from. The scope
    // effect owns the reset, so the insight resumes once it has settled.
    if (activePortfolio) {
      pendingInsightRef.current = { view, intent };
      setActivePortfolio(null);
      return;
    }
    // Tile TITLES plus the controls that shaped the view — never raw tile ids.
    // Mirrors chat/view_insight.py::_view_question (see viewLabel.js).
    const label = describeView(view, ctx.propertyName);
    const nowIso = new Date().toISOString();
    setMessages((m) => [
      ...m,
      { role: "user", content: label, created_at: nowIso },
      { role: "assistant", content: "", created_at: nowIso },
    ]);
    setLoading(true);
    setStatus("");
    setSuggestions([]);
    setToolHints([]);
    setShowResume(false);
    setShowHistory(false);
    setFbCommentFor(null);

    const controller = new AbortController();
    abortRef.current = controller;
    let acc = "";
    const patchLast = (patch) =>
      setMessages((m) => {
        const copy = m.slice();
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", ...patch };
        return copy;
      });
    const tw = makeTypewriter((text) => patchLast({ content: text }));

    streamChat({
      propertyId: ctx.propertyId,
      view,
      intent,
      sessionId: sessionRef.current,
      signal: controller.signal,
      onStatus: (s) => setStatus(s),
      onSources: (list) => patchLast({ sources: Array.isArray(list) ? list : [] }),
      onSuggestions: (list) => {
        setSuggestions(Array.isArray(list) ? list.slice(0, 3) : []);
        setShowSuggestions(false);
      },
      onDelta: (d) => {
        acc += d;
        if (acc === d) setStatus("");
        tw.push(d);
      },
      onDone: (sid, messageId, _meta, engine) => {
        tw.snap();                        // feedback row/PDF/copy read the final DOM
        if (sid) {
          sessionRef.current = sid;
          setSessionId(sid);
        }
        patchLast({ id: messageId || undefined, created_at: new Date().toISOString(), feedback: "", engine: engine || "" });
        setStatus("");
        setLoading(false);
        loadSessions();
      },
      onError: (msg) => {
        tw.abort();                       // don't let the drain overwrite the error text
        patchLast({ content: msg || "Something went wrong. Please try again.", error: true });
        setStatus("");
        setLoading(false);
      },
      // Closing the panel, switching property or switching scope mid-summary all
      // abort this stream. Without a handler here it ended silently and left
      // `loading` true, which silently disables send — the composer accepted text
      // and Enter did nothing until the page was reloaded.
      onAbort: ({ streamed } = {}) => {
        tw.abort();
        patchLast({ content: (streamed || acc) ? acc : "Stopped before I got going.",
                    stopped: true });
        setStatus("");
        setLoading(false);
      },
    });
  };
  runInsightRef.current = runViewInsight; // keep the workspace-trigger listener fresh

  // Resume the held insight once the scope-change reset has run. Declared AFTER
  // the reset effect, so React runs them in that order and the fresh bubbles are
  // never wiped by the reset that was meant to precede them.
  useEffect(() => {
    if (activePortfolio) return;
    const pending = pendingInsightRef.current;
    if (!pending) return;
    pendingInsightRef.current = null;
    runViewInsight(pending.view, pending.intent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePortfolio]);

  const clearAll = () => {
    if (!ctx.propertyId) return;
    clearSessions(ctx.propertyId, activePortfolio ? activePortfolio.id : null).then(() => {
      setSessions([]);
      newChat();
    }).catch(() => {});
  };

  // Drag the left edge to resize the docked panel
  const onResizeStart = (e) => {
    e.preventDefault();
    const sx = e.clientX;
    const sw = widthRef.current;
    const move = (ev) => {
      const delta = sx - ev.clientX; // dragging left widens the panel
      const max = Math.min(900, window.innerWidth * 0.96);
      const w = Math.max(380, Math.min(max, sw + delta));
      widthRef.current = w;
      setWidth(w);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try { localStorage.setItem("wiz_width", String(Math.round(widthRef.current))); } catch (_) {}
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";
  };

  // Resize the maximized (centered) window from its bottom-right corner.
  const onCornerResizeStart = (e) => {
    e.preventDefault();
    const win = e.currentTarget.closest(".wiz-window");
    const rect = win.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, sw = rect.width, sh = rect.height;
    const move = (ev) => {
      // centered window grows symmetrically, so the corner moves 2x the size delta
      const w = Math.max(440, Math.min(window.innerWidth * 0.98, sw + (ev.clientX - sx) * 2));
      const h = Math.max(360, Math.min(window.innerHeight * 0.96, sh + (ev.clientY - sy) * 2));
      const s = { w: Math.round(w), h: Math.round(h) };
      maxSizeRef.current = s;
      setMaxSize(s);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try { localStorage.setItem("wiz_maxsize", JSON.stringify(maxSizeRef.current)); } catch (_) {}
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "nwse-resize";
  };

  // Resize the mini floater from any edge/corner. `dir` is a compass string of
  // n/s/e/w (e.g. "w", "s", "nw", "se"). The dragged edge follows the cursor;
  // the opposite edge stays anchored. The first resize DETACHES the panel to a
  // free-floating position so the right/bottom edges have room to move (by
  // default it's pinned to the screen corner).
  const MINI_CURSOR = { n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize", nw: "nwse-resize", se: "nwse-resize", ne: "nesw-resize", sw: "nesw-resize" };
  const startMiniResize = (dir) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const win = e.currentTarget.closest(".wiz-window");
    if (!win) return;
    const r = win.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const sw = r.width, sh = r.height, sl = r.left, stp = r.top;
    const W = dir.includes("w"), E = dir.includes("e"), N = dir.includes("n"), S = dir.includes("s");
    if (!miniPos) setMiniPos({ x: sl, y: stp });   // detach on first resize
    const maxW = Math.min(900, window.innerWidth * 0.96);
    const maxH = window.innerHeight * 0.92;
    let curW = sw, curH = sh, curX = sl, curY = stp;
    const move = (ev) => {
      if (W || E) {
        const dx = W ? sx - ev.clientX : ev.clientX - sx;
        curW = Math.max(380, Math.min(maxW, sw + dx));
        curX = W ? sl - (curW - sw) : sl;
        widthRef.current = curW;
        setWidth(curW);
      }
      if (N || S) {
        const dy = N ? sy - ev.clientY : ev.clientY - sy;
        curH = Math.max(340, Math.min(maxH, sh + dy));
        curY = N ? stp - (curH - sh) : stp;
        setMiniHeight(curH);
      }
      setMiniPos({ x: Math.max(4, curX), y: Math.max(4, curY) });
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try {
        if (W || E) localStorage.setItem("wiz_width", String(Math.round(curW)));
        if (N || S) localStorage.setItem("wiz_mini_h", String(Math.round(curH)));
      } catch (_) {}
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.body.style.userSelect = "none";
    document.body.style.cursor = MINI_CURSOR[dir] || "default";
  };

  // ---- Wide tables on paper -------------------------------------------------
  // Print has no scrollbars, so a table wider than the page is clipped rather
  // than scrolled. The print CSS already lets cells wrap, which fixes most
  // tables; this pass catches the rest by measuring each table against the
  // printable width and scaling it down — but only to a legibility FLOOR. Below
  // that we stop shrinking and leave it: unreadable 6pt output is worse than a
  // table that needs the follow-up landscape/column-chunking work (AL-64).
  const PRINT_WIDTH_PX = 700;      // A4 portrait, 12mm margins, at 96dpi
  const PRINT_WIDTH_LAND_PX = 960; // A4/Letter LANDSCAPE, same margins (Letter is the tighter of the two)
  const MIN_SCALE = 0.75;

  // AL-64. A 9-column table wants ~1,100px. Portrait gives 700, and the
  // legibility floor stops the shrinking at 0.75 — so it renders 825px wide on a
  // 700px page and the right-hand columns run off the sheet. That is why tables
  // "went missing" from exports.
  //
  // Named CSS pages fix it without making the prose worse: only the wide tables
  // get a landscape page, paragraphs stay portrait.
  //
  // GUARDED, because a wider budget on a page that stayed portrait would be WORSE
  // than today (scale 0.87 → 1,000px on a 700px page loses 300px instead of 125).
  // So the wider budget is used ONLY when the browser recognises the `page`
  // property; otherwise we keep the current portrait behaviour exactly.
  const SUPPORTS_NAMED_PAGES = (() => {
    try { return !!(window.CSS && CSS.supports && CSS.supports("page", "auto")); } catch (_) { return false; }
  })();

  const fitWideTables = (root) => {
    try {
      const tables = root.querySelectorAll(".wiz-table, .wiz-calc__subtable table");
      tables.forEach((table) => {
        // Left-align columns whose body cells are not numeric — right-aligned
        // prose looks broken once it wraps onto several lines.
        const rows = Array.from(table.tBodies[0] ? table.tBodies[0].rows : []);
        const headCells = table.tHead && table.tHead.rows[0] ? table.tHead.rows[0].cells : [];
        const colCount = headCells.length || (rows[0] ? rows[0].cells.length : 0);
        for (let c = 0; c < colCount; c += 1) {
          const values = rows.slice(0, 8).map((r) => (r.cells[c] ? r.cells[c].textContent.trim() : ""));
          const numeric = values.filter(Boolean).every((v) => /^[-+$€£]?[\d,.\s%kKmM()–-]+$/.test(v));
          if (!numeric) {
            if (headCells[c]) headCells[c].classList.add("lqx-text");
            rows.forEach((r) => { if (r.cells[c]) r.cells[c].classList.add("lqx-text"); });
          }
        }

        // Measure AFTER the wrap/typography rules would apply. The print CSS only
        // takes effect in print media, so approximate here with the natural width;
        // scrollWidth on the wrapper is the honest "how wide does it want to be".
        const wrap = table.closest(".wiz-table-wrap") || table.parentElement;
        const natural = Math.max(table.scrollWidth, wrap ? wrap.scrollWidth : 0);
        if (!natural || natural <= PRINT_WIDTH_PX) return;

        // Too wide for portrait: give it a landscape page and the wider budget.
        // Falls back to the portrait budget when named pages aren't supported, so
        // this can never render worse than it does today.
        const landscape = SUPPORTS_NAMED_PAGES && natural > PRINT_WIDTH_PX;
        const budget = landscape ? PRINT_WIDTH_LAND_PX : PRINT_WIDTH_PX;

        // clamp: never enlarge, never shrink past the legibility floor
        const scale = Math.min(1, Math.max(MIN_SCALE, budget / natural));
        const box = document.createElement("div");
        box.className = landscape ? "lqx-fit-box lqx-land" : "lqx-fit-box";
        const inner = document.createElement("div");
        inner.className = "lqx-fit";
        inner.style.transform = `scale(${scale.toFixed(3)})`;
        inner.style.width = `${natural}px`;
        (wrap || table).parentNode.insertBefore(box, wrap || table);
        inner.appendChild(wrap || table);
        box.appendChild(inner);
        // scaling is visual only — reserve the collapsed height so the next
        // block doesn't overlap the table
        const h = inner.getBoundingClientRect().height || table.scrollHeight;
        box.style.height = `${Math.ceil(h * scale)}px`;
        // Only warn when the floor actually bit AND the table still overflows its
        // page. On a landscape page a 9-column table fits comfortably, so the old
        // unconditional warning would have been crying wolf.
        if (scale <= MIN_SCALE + 0.001 && natural * scale > budget + 1) {
          const note = document.createElement("div");
          note.className = "lqx-fit-note";
          note.textContent = "Very wide table — shown at the smallest readable size; "
            + "right-hand columns may still be cut. See the on-screen view for the full table.";
          box.appendChild(note);
        }
      });
    } catch (_) { /* never block the print on a layout tweak */ }
  };

  // ---- Download the conversation as PDF (client-side print view) ----
  // Clones the rendered messages (tables, Highcharts SVG, widget cards — exactly
  // as displayed), mounts them in a hidden print root IN THIS SAME DOCUMENT and
  // calls window.print(). Same-document printing is the reliability fix (AL-9/25):
  // the old off-screen-iframe + document.write approach re-loaded every stylesheet
  // into about:blank and fired print() after a timing guess — when the CSS bundle
  // lost that race the PDF came out blank/unstyled. Here every style is already
  // loaded, so there is nothing to wait for and no race to lose.
  const exportPdf = (ev) => {
    // Shift+click = DEBUG PREVIEW: render the exact print document on screen
    // instead of printing, so layout issues are diagnosable with a screenshot.
    const debugPreview = !!(ev && ev.shiftKey);
    const win = document.querySelector(".wiz-window");
    const src = win && win.querySelector(".wiz-messages");
    if (!src) return;

    // Force LIGHT theme while we clone + read computed colors (restored below).
    // The whole pass is synchronous, so the user never sees the flip.
    const prevTheme = win.getAttribute("data-theme");
    if (prevTheme === "dark") win.setAttribute("data-theme", "light");

    const clone = src.cloneNode(true);

    // Make colors PRINT-PROOF. Browsers suppress background colors when printing
    // and `print-color-adjust: exact` is unreliable (notably for iframe print),
    // so the calendar heat bands, verdict banners, table shading and legend
    // swatches would vanish to white. Fix: for every element with a background,
    // paint a same-color inline SVG <rect> BEHIND its text — SVG fills are
    // foreground vector content and print unconditionally. Foreground colors
    // (text, borders) are also inlined for fidelity. Runs while `src` is on-page.
    const splitColor = (c) => {
      const m = String(c).match(/rgba?\(([^)]+)\)/i);
      if (m) {
        const p = m[1].split(",").map((s) => s.trim());
        return { fill: `rgb(${p[0]}, ${p[1]}, ${p[2]})`, op: p[3] !== undefined ? p[3] : "1" };
      }
      return { fill: c, op: "1" };
    };
    const firstColor = (grad) => {
      const m = String(grad).match(/rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}/);
      return m ? m[0] : null;
    };
    const NS = "http://www.w3.org/2000/svg";
    // Elements that can't (or mustn't) receive child nodes — injecting a fill
    // layer into these throws or corrupts them, which would abort the export.
    const NO_INJECT = new Set(["INPUT", "TEXTAREA", "SELECT", "IMG", "HR", "BR",
      "CANVAS", "VIDEO", "AUDIO", "IFRAME", "OBJECT", "EMBED", "BUTTON"]);
    // Table-structure elements may contain ONLY their proper children (a <tr> may
    // hold only cells). Injecting a fill layer into one makes the browser wrap it
    // in an anonymous cell, which shifts every real cell one column right — this
    // is what broke the zebra-striped (even) rows in exported PDFs. For a striped
    // ROW we paint its CELLS instead; the other structure tags are left alone.
    const TABLE_STRUCT = new Set(["TABLE", "THEAD", "TBODY", "TFOOT", "TR", "COLGROUP", "COL"]);
    // `el` is the CLONE (still detached at this point, so getComputedStyle on it
    // returns nothing) — positioning must be read from `srcEl`, the live element.
    // Getting this wrong lets every absolutely-positioned fill layer escape its
    // own box and paint against the nearest positioned ancestor.
    const injectFill = (el, color, srcEl) => {
      const { fill, op } = splitColor(color);
      const pos = srcEl ? window.getComputedStyle(srcEl).position : "";
      if (pos === "static" || !pos) el.style.position = "relative";
      el.style.isolation = "isolate";
      el.style.overflow = "hidden";
      const layer = document.createElementNS(NS, "svg");
      layer.setAttribute("aria-hidden", "true");
      layer.setAttribute("preserveAspectRatio", "none");
      layer.setAttribute("width", "100%");
      layer.setAttribute("height", "100%");
      layer.style.cssText = "position:absolute;left:0;top:0;width:100%;height:100%;z-index:-1;border-radius:inherit;pointer-events:none;";
      const rect = document.createElementNS(NS, "rect");
      rect.setAttribute("x", "0"); rect.setAttribute("y", "0");
      rect.setAttribute("width", "100%"); rect.setAttribute("height", "100%");
      rect.setAttribute("fill", fill);
      if (op !== "1") rect.setAttribute("fill-opacity", op);
      layer.appendChild(rect);
      el.insertBefore(layer, el.firstChild);
    };
    const paint = (from, to) => {
      const cs = window.getComputedStyle(from);
      if (cs.display === "none") return;
      // foreground (always prints) — keep text + border colors faithful
      if (cs.color) to.style.color = cs.color;
      ["Top", "Right", "Bottom", "Left"].forEach((s) => {
        if (parseFloat(cs["border" + s + "Width"]) > 0) to.style["border" + s + "Color"] = cs["border" + s + "Color"];
      });
      // never inject inside an <svg> (Highcharts etc. already carry their fills),
      // nor into form controls / void / replaced elements (can't hold children)
      if (to.namespaceURI === NS || (to.tagName && to.tagName.toLowerCase() === "svg")) return;
      if (NO_INJECT.has(to.tagName)) return;
      const bg = cs.backgroundColor;
      const bgi = cs.backgroundImage;
      let color = bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent" ? bg
        : (bgi && bgi !== "none" ? firstColor(bgi) : null);
      if (!color) return;
      // A striped row: hand the colour down to its cells (a <tr> can't hold the
      // layer itself). Cells with their own background are skipped — they get
      // painted on their own pass and must keep their own colour.
      if (TABLE_STRUCT.has(to.tagName)) {
        if (to.tagName === "TR") {
          Array.from(to.children).forEach((cell, i) => {
            const fromCell = from.children[i];
            if (!fromCell) return;
            const cbg = window.getComputedStyle(fromCell).backgroundColor;
            const ownBg = cbg && cbg !== "rgba(0, 0, 0, 0)" && cbg !== "transparent";
            if (!ownBg) injectFill(cell, color, fromCell);
          });
        }
        return;
      }
      injectFill(to, color, from);
    };
    // Pseudo-element accent bar on .wiz-calc / cards — painted as its own SVG strip.
    const paintAccentBar = (from, to) => {
      const be = window.getComputedStyle(from, "::before");
      if (!be || be.content === "none" || !be.backgroundImage || be.backgroundImage === "none") return;
      const color = firstColor(be.backgroundImage) || firstColor(be.backgroundColor);
      if (!color) return;
      const { fill, op } = splitColor(color);
      const h = be.height && be.height !== "auto" ? be.height : "3px";
      // read position from the LIVE element, not the detached clone (see injectFill)
      const barPos = window.getComputedStyle(from).position;
      if (barPos === "static" || !barPos) to.style.position = "relative";
      const bar = document.createElementNS(NS, "svg");
      bar.setAttribute("preserveAspectRatio", "none");
      bar.style.cssText = `position:absolute;left:0;top:0;width:100%;height:${h};z-index:0;pointer-events:none;`;
      const r = document.createElementNS(NS, "rect");
      r.setAttribute("width", "100%"); r.setAttribute("height", "100%"); r.setAttribute("fill", fill);
      if (op !== "1") r.setAttribute("fill-opacity", op);
      bar.appendChild(r);
      to.style.overflow = "hidden";
      to.insertBefore(bar, to.firstChild);
    };
    // Whole color pass is best-effort: if anything throws, the PDF must still
    // print (just without a color or two) rather than silently doing nothing.
    try {
      const fromEls = src.querySelectorAll("*");
      const toEls = clone.querySelectorAll("*");
      paint(src, clone);
      for (let i = 0; i < toEls.length; i++) {
        if (!fromEls[i]) continue;
        try {
          paint(fromEls[i], toEls[i]);
          if (toEls[i].className && String(toEls[i].className).indexOf("wiz-calc") === 0) {
            paintAccentBar(fromEls[i], toEls[i]);
          }
        } catch (_) { /* keep going */ }
      }
    } catch (_) { /* colors are optional; never block the export */ }

    // Form-control state (calculator inputs, quote fields…) isn't carried by
    // cloneNode — copy each control's live value into the clone's attributes.
    const orig = src.querySelectorAll("input, select, textarea");
    const dup = clone.querySelectorAll("input, select, textarea");
    orig.forEach((el, i) => {
      const ce = dup[i];
      if (!ce) return;
      if (el.tagName === "TEXTAREA") ce.textContent = el.value;
      else if (el.tagName === "SELECT") {
        Array.from(ce.options || []).forEach((op) => {
          if (op.value === el.value) op.setAttribute("selected", "selected");
          else op.removeAttribute("selected");
        });
      } else if (el.type === "checkbox" || el.type === "radio") {
        if (el.checked) ce.setAttribute("checked", "checked");
        else ce.removeAttribute("checked");
      } else ce.setAttribute("value", el.value);
    });

    // Restore the user's theme — clone + color pass are done reading styles.
    if (prevTheme === "dark") win.setAttribute("data-theme", prevTheme);

    const asofMsg = [...messages].reverse().find((m) => m.context_as_of);
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // The export is titled with the SCOPE the conversation was asked in, not with
    // whichever hotel happens to be selected. A portfolio conversation headed
    // "LodgIQ Demo Hotel" reads, to anyone the PDF is forwarded to, as three
    // hotels' numbers reported under one hotel's name.
    //
    // Read from the MESSAGES, not from the picker: an exported conversation is a
    // record of what was asked, and the user may well have changed scope before
    // hitting export.
    const msgScope = (messages.find((m) => m.pfScope) || {}).pfScope
      || (activePortfolio ? activePortfolio.name : "");
    const scopeName = msgScope || ctx.propertyName || "Ask LodgIQ";
    const scopeCount = (msgScope && activePortfolio && activePortfolio.name === msgScope
      ? activePortfolio.property_count : null);
    const hotel = esc(scopeName + (scopeCount ? ` · ${scopeCount} hotels` : ""));
    const stamp = new Date().toLocaleString();
    const asof = asofMsg ? fullStamp(asofMsg.context_as_of) : null;

    // Build the print document as real DOM in THIS document. All app styles are
    // already loaded here, so the clone renders identically with zero waiting.
    const root = document.createElement("div");
    root.id = "lqx-print-root";
    root.setAttribute("aria-hidden", "true");
    root.innerHTML =
      `<div class="lqx-page">
        <div class="lqx-head"><span class="lqx-hotel">${hotel}</span><span class="lqx-tag">Ask LodgIQ conversation</span></div>
        <div class="lqx-meta">Exported ${esc(stamp)}${asof ? ` · Grounded in data as of ${esc(asof)}` : ""}</div>
        <div class="wiz-window lqx-body" data-theme="light"></div>
        <div class="lqx-foot"><span>AI-generated by Ask LodgIQ — AI can make mistakes; verify important figures. © ${new Date().getFullYear()} LodgIQ</span><span>${hotel}</span></div>
      </div>`;
    root.querySelector(".lqx-body").appendChild(clone);   // real node, keeps injected SVG fills

    // Print-only stylesheet: on screen the root is display:none; during print the
    // root is the ONLY thing shown. body-direct-children covers #root + portals.
    const style = document.createElement("style");
    style.id = "lqx-print-style";
    style.textContent = `
  #lqx-print-root { display: none; }
  /* THE historical blank-PDF root cause: the clone wrapper carries .wiz-window,
     whose wiz-slide-in entry animation STARTS AT opacity:0. The print root goes
     display:none -> visible at print time, the animation restarts, and the print
     engine snapshots frame 0 = a fully transparent conversation. Kill every
     animation/transition inside the print root. transform is reset ONLY on the
     wrapper (a global transform:none would collapse Highcharts' inner <g> layout). */
  #lqx-print-root *, #lqx-print-root { animation: none !important; transition: none !important; }
  #lqx-print-root .lqx-body { transform: none !important; opacity: 1 !important; }
  /* AL-64. There was NO @page rule at all — the export ran on browser defaults.
     Declaring the default keeps every prose page portrait, and the named page
     gives wide tables a landscape sheet.

     A page-name change forces a page break by itself, so no explicit
     break-before/after is needed here; adding one produces stray blank pages.
     The lqx-land class is only applied when CSS.supports("page", ...) is true —
     see fitWideTables — so an older browser silently keeps today's behaviour. */
  @page { size: A4 portrait; margin: 12mm; }
  @page lqx-wide { size: A4 landscape; margin: 12mm; }

  @media print {
    /* The table's own page. max-width is deliberately NOT set: the transform in
       .lqx-fit has already scaled the content to the landscape budget, and a
       max-width here would clip what the scale just made fit. */
    #lqx-print-root .lqx-land { page: lqx-wide; }
    /* The page wrapper caps everything at 780px for portrait reading — a
       landscape table has to escape that or the extra width is wasted. */
    #lqx-print-root .lqx-page .lqx-land { max-width: none; width: auto; }

    body.lqx-printing > *:not(#lqx-print-root) { display: none !important; visibility: hidden !important; }
    body.lqx-printing #lqx-print-root { display: block !important; visibility: visible !important;
      position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important;
      font-family: "SFUI Regular", "Lato", -apple-system, sans-serif; color: #333; background: #fff; }
    body.lqx-printing { background: #fff !important; height: auto !important; overflow: visible !important; }
    #lqx-print-root * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
      pointer-events: none !important; }
    #lqx-print-root .lqx-page { max-width: 780px; margin: 0 auto; }
    #lqx-print-root .lqx-head { border-bottom: 3px solid #01a4ef; padding-bottom: 12px; margin-bottom: 20px;
      display: flex; justify-content: space-between; align-items: baseline; }
    #lqx-print-root .lqx-hotel { font-size: 20px; font-weight: 700; color: #00415f; }
    #lqx-print-root .lqx-tag { font-size: 10.5px; color: #7a8894; letter-spacing: 1.5px; text-transform: uppercase; }
    #lqx-print-root .lqx-meta { font-size: 11px; color: #7a8894; margin: -12px 0 22px; }
    #lqx-print-root .lqx-foot { margin-top: 34px; padding-top: 10px; border-top: 1px solid #e8ecef;
      font-size: 10px; color: #9aa5ae; display: flex; justify-content: space-between; }
    /* the conversation, print-adapted: neutralise ALL the floating-window sizing
       (.wiz-window is width:540px; height:100vh; overflow:hidden — would clip) */
    #lqx-print-root .lqx-body { display: block !important; width: auto !important; height: auto !important;
      max-width: none !important; max-height: none !important; min-height: 0 !important;
      overflow: visible !important; position: static !important; border-radius: 0 !important;
      border: 0 !important; box-shadow: none !important; background: transparent !important; }
    #lqx-print-root .wiz-messages { overflow: visible !important; height: auto !important; max-height: none !important;
      padding: 0 !important; background: transparent !important; }
    /* ---- PAGINATION -------------------------------------------------------
       An answer bubble must be allowed to FLOW across pages. It used to carry
       break-inside: avoid, so any answer taller than the space left was pushed
       whole to the next page — a half-empty page before every long answer, and
       on a ten-page export that is pages of white.

       What stays atomic is only what becomes unreadable when split: a table ROW,
       a widget card, a chart. Prose is not in that set. Orphans/widows stop a
       single stranded line, which is the cosmetic problem that made "avoid" look
       like the right tool in the first place. */
    #lqx-print-root .wiz-msg, #lqx-print-root .wiz-msg__bubble {
      break-inside: auto; page-break-inside: auto; }
    #lqx-print-root .wiz-msg__bubble p,
    #lqx-print-root .wiz-msg__bubble li { orphans: 3; widows: 3; }
    /* keep a heading with the text it introduces, and the meta row with its answer */
    #lqx-print-root .wiz-msg__bubble h1, #lqx-print-root .wiz-msg__bubble h2,
    #lqx-print-root .wiz-msg__bubble h3, #lqx-print-root .wiz-msg__bubble h4 {
      break-after: avoid; page-break-after: avoid; }
    #lqx-print-root .wiz-msg__meta { break-before: avoid; page-break-before: avoid; }
    /* Widgets stay whole ONLY where a split destroys them: a chart cut in half
       is unreadable, a calculator's steps out of order are worse than useless.
       A card taller than a page still breaks anyway — avoid is a preference,
       not a guarantee.

       .wiz-tablecard is deliberately NOT here. It WRAPS the table, so making it
       atomic pins a 30-row table to one page and undoes the pagination fix one
       level up. Tables flow; their rows and header handle readability. */
    #lqx-print-root .wiz-calc, #lqx-print-root .wiz-chart,
    #lqx-print-root .wiz-sim, #lqx-print-root .wiz-quote,
    #lqx-print-root .wiz-brief { break-inside: avoid; page-break-inside: avoid; }
    #lqx-print-root .wiz-tablecard { break-inside: auto; page-break-inside: auto; }
    /* The demand calendar is a month grid: let it break BETWEEN months and keep
       each month whole, rather than stranding a 30-day card on its own page.
       Its root carries BOTH wiz-calc and wiz-cal, so this needs the two-class
       selector to outrank the .wiz-calc rule above — matching on source order
       alone would silently flip if these lines were ever reordered. */
    #lqx-print-root .wiz-calc.wiz-cal { break-inside: auto; page-break-inside: auto; }
    #lqx-print-root .wiz-cal__month { break-inside: avoid; page-break-inside: avoid; }
    /* A footer alone on a final page is the same "pushed whole" ugliness in
       miniature — keep it with the content it closes where it can. */
    #lqx-print-root .lqx-foot { break-before: avoid; page-break-before: avoid; }
    #lqx-print-root .wiz-msg__bubble { box-shadow: none !important; }
    /* interactive-only chrome: hidden on paper */
    #lqx-print-root .wiz-msg__more, #lqx-print-root .wiz-msg__menu,
    #lqx-print-root .wiz-msg__copy, #lqx-print-root .wiz-fb, #lqx-print-root .wiz-fb__commentbox,
    #lqx-print-root .wiz-msg__engine, #lqx-print-root .wiz-typing, #lqx-print-root .wiz-status,
    #lqx-print-root .wiz-calc__interpret, #lqx-print-root .wiz-calc__run, #lqx-print-root .wiz-calc__addrow,
    #lqx-print-root .wiz-calc__rowdel, #lqx-print-root .wiz-calc__math-toggle, #lqx-print-root .wiz-sim__quick,
    #lqx-print-root .wiz-quote__actions, #lqx-print-root .wiz-cal__legend-note, #lqx-print-root .wiz-support__report,
    #lqx-print-root .wiz-support__err, #lqx-print-root .wiz-tablecard__bar, #lqx-print-root .wiz-cal__mode,
    /* briefing card: the dismiss X, the follow-up button and the deep-link chips
       are all things to CLICK — meaningless on paper, and the X printed as a
       stray glyph beside the title. */
    #lqx-print-root .wiz-brief__close, #lqx-print-root .wiz-brief__follow,
    #lqx-print-root .wiz-brief__links, #lqx-print-root .wiz-brief__sources { display: none !important; }
    #lqx-print-root .wiz-calc__steps { display: block !important; }
    /* AL-94: scope is NOT interactive chrome — a reader of the PDF must be able
       to tell a portfolio answer from a single-hotel one. The glyph carries no
       meaning on paper, so swap it for the words. */
    #lqx-print-root .wiz-msg__scope-ic { display: none !important; }
    #lqx-print-root .wiz-msg__scope-txt { display: inline !important; }
    #lqx-print-root input, #lqx-print-root select, #lqx-print-root textarea {
      border-color: #e0e4e8 !important; background: #fff !important; }

    /* ---- WIDE TABLES ------------------------------------------------------
       On screen a table scrolls sideways inside .wiz-table-wrap. Paper has no
       scrollbars, so that overflow was simply CLIPPED — the right-hand columns
       vanished. Three fixes, cheapest first:
         1. let the wrapper overflow visibly, and let cells WRAP (nowrap was
            forcing a prose column like "Action" to one endless line);
         2. shrink type and padding a little to buy width;
         3. anything still too wide is scaled to fit by the print-prep pass
            (.lqx-fit, below), down to a legibility floor.                     */
    #lqx-print-root .wiz-table-wrap, #lqx-print-root .wiz-calc__subtable {
      overflow: visible !important; max-width: none !important; }
    #lqx-print-root .wiz-table, #lqx-print-root .wiz-calc__subtable table {
      table-layout: auto !important; width: 100% !important; font-size: 9.5px !important; }
    #lqx-print-root .wiz-table th, #lqx-print-root .wiz-table td,
    #lqx-print-root .wiz-calc__subtable th, #lqx-print-root .wiz-calc__subtable td {
      white-space: normal !important;      /* the actual clipping culprit */
      overflow-wrap: break-word; word-break: break-word;
      padding: 3px 5px !important; }
    /* the sticky key column is a SCREEN affordance (it pins while you scroll
       sideways). Paper doesn't scroll, and a stuck cell prints displaced — so
       release it and drop its divider shadow. */
    #lqx-print-root .wiz-table--wide th:first-child,
    #lqx-print-root .wiz-table--wide td:first-child {
      position: static !important; box-shadow: none !important; background: transparent !important; }
    /* numbers stay right-aligned; prose columns read badly right-aligned once
       they wrap, so anything that is not a number goes left */
    #lqx-print-root .wiz-table td.lqx-text, #lqx-print-root .wiz-table th.lqx-text {
      text-align: left !important; }
    /* repeat the header on every page and never split a row across pages */
    #lqx-print-root thead { display: table-header-group; }
    #lqx-print-root tr { break-inside: avoid; page-break-inside: avoid; }
    /* scale-to-fit wrapper written by the print-prep pass */
    #lqx-print-root .lqx-fit { transform-origin: top left; }
    /* The box may BREAK — thead repeats on each page and rows stay whole, so a
       30-row table continues across pages rather than being shunted onto the
       next one entirely. (A scaled .lqx-fit inside cannot split, since a
       transform is one box; those stay atomic by nature.) */
    #lqx-print-root .lqx-fit-box { overflow: visible !important;
      break-inside: auto; page-break-inside: auto; }
    #lqx-print-root .lqx-fit-note {
      font-size: 8px; color: #9aa5ae; margin-top: 2px; font-style: italic; }
  }`;

    document.body.appendChild(style);
    document.body.appendChild(root);
    fitWideTables(root);

    const cleanup = () => {
      try { document.body.classList.remove("lqx-printing"); } catch (_) {}
      try { root.remove(); } catch (_) {}
      try { style.remove(); } catch (_) {}
      window.removeEventListener("afterprint", cleanup);
    };

    if (debugPreview) {
      // On-screen render of the print document (screen media, same DOM): a white
      // overlay above the app + a close button. Print CSS differences aside, if
      // content is missing HERE the clone/DOM is broken; if it looks right here
      // but prints wrong, the fault is in the @media print rules.
      const dbg = document.createElement("style");
      dbg.textContent = `#lqx-print-root { display: block !important; position: fixed !important;
        inset: 0 !important; overflow: auto !important; background: #fff !important;
        z-index: 2147483647 !important; padding: 24px !important; }`;
      document.body.appendChild(dbg);
      const btn = document.createElement("button");
      btn.textContent = "✕ close print preview";
      btn.style.cssText = "position:sticky;top:0;float:right;z-index:2;padding:6px 12px;" +
        "background:#01a4ef;color:#fff;border:0;border-radius:6px;cursor:pointer;";
      btn.onclick = () => { try { dbg.remove(); } catch (_) {} cleanup(); };
      root.insertBefore(btn, root.firstChild);
      return;
    }

    document.body.classList.add("lqx-printing");
    window.addEventListener("afterprint", cleanup);
    // window.print() blocks until the dialog closes in Chrome/Firefox; Safari is
    // async — afterprint covers it, with a timeout safety net either way.
    try { window.print(); } catch (_) { cleanup(); return; }
    setTimeout(cleanup, 120000);
  };

  // ---- A/B/C compare mode (AB testers only) ----
  const toggleCompareSel = (key) =>
    setCompareSel((sel) =>
      sel.includes(key) ? sel.filter((k) => k !== key) : sel.length >= 4 ? sel : [...sel, key]
    );

  const patchVariant = (group, vi, patch) =>
    setMessages((m) =>
      m.map((msg) => {
        if (msg.role !== "compare" || msg.group !== group) return msg;
        const variants = msg.variants.slice();
        variants[vi] = { ...variants[vi], ...patch };
        return { ...msg, variants };
      })
    );

  const nowMs = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

  const sendCompare = (text) => {
    const q = (text !== undefined && text !== null ? text : input).trim();
    if (!q || loading) return;

    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";

    if (!ctx.propertyId) {
      setMessages((m) => [
        ...m,
        { role: "user", content: q },
        { role: "assistant", content: "Select a property first — I need one to pull your revenue data.", error: true },
      ]);
      return;
    }

    const picks = (compareSel.length ? compareSel : abProfiles.slice(0, 2).map((p) => p.key)).slice(0, 4);
    if (!picks.length) return;

    const group = `cmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const labelFor = (k) => (abProfiles.find((p) => p.key === k) || {}).label || k;
    const variants = picks.map((k) => ({
      key: k, label: labelFor(k), content: "", status: "", error: null,
      done: false, tokens: null, latencyMs: null, resultId: null, rating: 0,
    }));

    setMessages((m) => [...m, { role: "compare", question: q, group, created_at: new Date().toISOString(), variants }]);
    setLoading(true);
    setStatus("");
    setSuggestions([]);
    setToolHints([]);
    setShowResume(false);
    setShowComparePicker(false);

    const controller = new AbortController();
    abortRef.current = controller;
    let remaining = picks.length;
    const settle = () => { if (--remaining <= 0) setLoading(false); };

    picks.forEach((key, vi) => {
      const started = nowMs();
      let acc = "";
      streamChat({
        propertyId: ctx.propertyId,
        message: q,
        profile: key,
        evalMode: true,
        evalGroup: group,
        signal: controller.signal,
        onStatus: (s) => patchVariant(group, vi, { status: s }),
        onDelta: (d) => { acc += d; patchVariant(group, vi, { content: acc, status: "" }); },
        onDone: (_sid, _mid, meta) => {
          patchVariant(group, vi, {
            done: true,
            resultId: (meta && meta.evalResultId) || null,
            tokens: meta ? meta.totalTokens : null,
            latencyMs: meta && meta.latencyMs != null ? meta.latencyMs : Math.round(nowMs() - started),
          });
          settle();
        },
        onError: (msg) => { patchVariant(group, vi, { error: msg || "Failed", done: true }); settle(); },
        // Same trap as the insight path: without this, an aborted variant never
        // settles, `remaining` never reaches zero and `loading` stays true.
        onAbort: () => { patchVariant(group, vi, { stopped: true, done: true }); settle(); },
      });
    });
  };

  const rateVariant = (group, vi, variant, star) => {
    if (!variant.resultId) return;
    const next = variant.rating === star ? 0 : star;
    patchVariant(group, vi, { rating: next });
    rateEval(ctx.propertyId, variant.resultId, next);
  };

  // Deep-link to the dashboard view a source came from, then minimize the chat so
  // the user can validate the answer against the live chart/table.
  // Phase 2: also sync the stay-date range and (for OTB) the segmentation.
  const navigateToSource = (src) => {
    const reg = registryFor(src);
    if (!reg) return;

    // ROUTE sources (e.g. GroupIQ): a dedicated page, not a dashboard tile.
    // newTab entries open in a NEW TAB (matching how the main app opens GroupIQ /
    // analytics-reports); the chat stays as-is. Safe from popup blockers because
    // this runs in the chip's click handler (user gesture). No tile/date syncing.
    if (reg.route) {
      // Carry the ask's context so the page opens ready to use (GroupIQ: group name
      // + check-in/check-out). PREFILL ONLY — the page never auto-submits, because
      // submitting calls the DS engine and saves a proposal.
      let route = reg.route;
      if (reg.prefillParams) {
        const p = (src && src.params) || {};
        const qs = new URLSearchParams();
        if (p.date_from) qs.set("from", p.date_from);
        if (p.date_to) qs.set("to", p.date_to);
        if (p.group_name) qs.set("name", p.group_name);
        if (qs.toString()) route = `${route}?${qs.toString()}`;
      }
      if (reg.newTab) {
        try { window.open(route, "_blank", "noopener"); } catch (_) {}
      } else {
        try { navigate(route); } catch (_) {}
        setMode("mini");
      }
      return;
    }

    const params = (src && src.params) || {};
    const grain = params.grain || "hotel";
    const segmented = src.domain === "otb" && grain !== "hotel" && !!GRAIN_TO_SEGMENT[grain] && !!reg.segmentedTileId;
    const tileId = segmented ? reg.segmentedTileId : reg.tileId;

    // NEVER dispatch a tile id the dashboard doesn't know. getTileById returns
    // null for an unknown id and DashboardLayout dereferences it without a guard
    // (`tileDetails.chartConfig`), so one bad id in the registry white-screens the
    // whole app with no way back — which is exactly what a DATA-SOURCE id used in
    // place of a TILE id did to the Room-Type Pricing chip.
    //
    // A chip that quietly does nothing is a small annoyance; a chip that kills the
    // app is a support call. Fail closed, and leave a breadcrumb so the next bad
    // id is a one-minute diagnosis rather than an hour of console archaeology.
    try {
      if (!getTileById(tileId)) {
        // eslint-disable-next-line no-console
        console.warn(`[Ask LodgIQ] source "${src.domain}" points at unknown tile "${tileId}" — `
          + "navigation skipped. Ids in sourceRegistry.js must be keys of TILES (app/config-v4-api.js).");
        return;
      }
    } catch (_) { return; }

    // The tile exists — but is it enabled for THIS user on THIS property? Same
    // gate Workspace uses to raise its LockLayer (hooks/use-product-variant).
    // Without this the chip navigates cleanly to "Room Pricing is not enabled for
    // you. Would you like to request access?" — a dead end reached by clicking a
    // button we offered. Say it in the chat instead and stay put.
    try {
      const allowed = hasFeature([tileId], "tiles")
        || hasFeature([tileId], "reports")
        || hasFeature([tileId], "logs");
      if (!allowed) {
        setDeepLinkNote(`${reg.label} isn't enabled for your account on this property, `
          + "so it can't be opened from here — the figures above still stand.");
        return;
      }
    } catch (_) { /* gate unavailable: fall through rather than block navigation */ }

    try { if (window.location && window.location.pathname !== "/") navigate("/"); } catch (_) {}

    // 0) The dashboard renders the tile from the module-level APP_CACHE.active_tiles
    //    (falling back to Redux only when empty). handleTileClick sets it on normal
    //    clicks, so we must set it too — otherwise the view stays on the cached tile.
    try { APP_CACHE.active_tiles = [tileId]; } catch (_) {}

    // 1) Switch the active view (thunk also sets measures + a tile-default range).
    // isLock MUST be cleared here. Workspace sets it true when the active tile
    // isn't in the user's product variant, but the ONLY thing that ever sets it
    // back to false is handleTileClick — a real DOM click on a dashboard tile
    // (`e.currentTarget.id`). A deep link never goes through that handler, so one
    // bad landing latched the lock and EVERY later chip rendered "… is not
    // enabled for you", whatever tile it opened, until the user clicked a tile by
    // hand. We only reach this line once the permission guard above has passed,
    // so false is the correct value.
    dispatch(updateAppState("change_active_tiles",
      { ui_state: { active_tiles: [tileId], isLock: false } }, "ask_q_source"));

    // 2) Read FRESH state (post-thunk), then apply controls + date range in one dispatch.
    const fresh = (store.getState() || {}).app?.current_state?.app_state || {};
    const controls = { ...(fresh.workspace_controls || {}) };
    if (reg.scopeType) controls.scopeType = reg.scopeType;
    if (src.domain === "otb") {
      controls.segmentType = segmented ? grain : "hotel";
      controls.segmented_view = segmented;
    }

    const appPatch = {
      data_load: true,
      kpi_load: !segmented,   // segmentation reuses KPIs (see MultiSegmentPicker); date/view changes reload them
      workspace_controls: controls,
    };

    // AS-OF: the answer's snapshot WINS. Clicking a source chip means "show me
    // the data behind THIS answer", so the tile has to open on the snapshot the
    // answer was computed from — otherwise the chip lands you on numbers that
    // disagree with the ones you clicked, and there is no way to reconcile them.
    //
    // The precedence used to run the other way, and it broke two ways at once:
    //   * an answer asked for as-of 1 July opened the tile on TODAY, because
    //     `fresh` was preferred over params.as_of;
    //   * an as-of the USER had set was also lost, because the
    //     change_active_tiles thunk above resets the range to the tile default
    //     BEFORE we read `fresh` — so `fresh` held the default, not their choice.
    // Reading the answer's own params fixes both, and is the only value that is
    // guaranteed to match the figures on screen.
    const asOf = params.as_of
      || fresh.primary_stay_range?.as_of_date
      || fresh.as_of_date
      || undefined;
    if (src.domain === "booking_curve" && params.date_from) {
      appPatch.primary_stay_range = { as_of_date: asOf, date_from: params.date_from, date_until: params.date_from };
      appPatch.date_range_type = "scopeTypeChanged_Dba"; // single-date pace curve
    } else if (params.date_from && params.date_to) {
      appPatch.primary_stay_range = { as_of_date: asOf, date_from: params.date_from, date_until: params.date_to };
      appPatch.date_range_type = "range";                // respects the explicit range
    }

    // The as-of lives in TWO places and needs BOTH: `primary_stay_range.as_of_date`
    // (what the range carries) and the top-level `app_state.as_of_date` (what
    // WorkspaceControls hands the picker). Writing only the first is why the
    // header kept showing the current as-of.
    if (asOf) appPatch.as_of_date = asOf;

    // TWO dispatches, and the ORDER is the whole fix.
    //
    // `date_range_type` is a single string and the picker's branches are mutually
    // exclusive (SmartDatePicker/index.js): 'range' applies date_from/until and
    // then calls applyChange() to fetch; 'asOfChanged' seeds the as-of and does
    // NOT fetch. Sending 'range' alone — which is what we did — dropped the
    // snapshot entirely.
    //
    // Crucially, applyChange() builds the request from the picker's LOCAL
    // `tempDateRange`, not from app_state:
    //     APP_CACHE.stay_range = [tempDateRange.date_from, ..., tempDateRange.as_of_date]
    // so writing app_state.as_of_date alone changes nothing about what is
    // fetched. 'asOfChanged' is the only path that updates tempDateRange.as_of_date.
    //
    // Hence: seed the as-of FIRST, then fire the range on the next tick so the
    // fetch reads a tempDateRange that already carries the right snapshot.
    const answerAsOf = params.as_of;
    const currentAsOf = fresh.as_of_date || fresh.primary_stay_range?.as_of_date;
    const needsAsOfHop = !!answerAsOf && answerAsOf !== currentAsOf;

    if (needsAsOfHop) {
      dispatch({
        type: UPDATE_STATE,
        payload: {
          app_state: {
            as_of_date: answerAsOf,
            primary_stay_range: {
              ...(fresh.primary_stay_range || {}),
              ...(appPatch.primary_stay_range || {}),
              as_of_date: answerAsOf,
            },
            date_range_type: "asOfChanged",
          },
          ui_state: { showspinner: true, isLock: false },
        },
      });
      setTimeout(() => {
        try {
          dispatch({ type: UPDATE_STATE, payload: { app_state: appPatch, ui_state: { showspinner: true, isLock: false } } });
        } catch (_) {}
      }, 0);
    } else {
      dispatch({ type: UPDATE_STATE, payload: { app_state: appPatch, ui_state: { showspinner: true, isLock: false } } });
    }

    setShowHistory(false);
    setMode("mini");   // half-height: the answer stays visible alongside the chart/table
    flashWorkspace();  // draw the eye to the view we just opened
  };

  // "Open combined view" — reuse the app's IntelliMerge: put multiple mergeable
  // tiles into ui_state.active_tiles (length>1) so the dashboard renders the merged
  // Table/Chart. Measures + merge-compatibility are recomputed by the app's own
  // change_active_tiles reducer and the tileMergeLogic effect (keyed on active_tiles),
  // so we drive it through the same channel rather than duplicating that logic.
  const openCombinedView = (sources) => {
    const tiles = mergeTilesForSources(sources);
    if (tiles.length < 2) return;

    try { if (window.location && window.location.pathname !== "/") navigate("/"); } catch (_) {}

    // Mirror handleTileClick/onTileMergeHandler: the workspace reads APP_CACHE first.
    try { APP_CACHE.active_tiles = tiles; } catch (_) {}

    // Reset to the base "Date" scope so the widest set of tiles is merge-compatible
    // (segmented / DBA scopes exclude some combinations).
    const controls = { ...(app_state.workspace_controls || {}), segmented_view: false, scopeType: "Date" };
    dispatch(updateAppState("change_active_tiles", {
      ui_state: { active_tiles: tiles },
      app_state: { workspace_controls: controls },
    }, "ask_q_combined"));

    // Apply the widest stay-date range across the merged sources (or just force a load).
    const fresh = (store.getState() || {}).app?.current_state?.app_state || {};
    const asOf = fresh.primary_stay_range?.as_of_date || fresh.as_of_date || undefined;
    const { from, to } = widestRange(sources);
    const patch = {};
    if (from && to) {
      patch.primary_stay_range = { as_of_date: asOf, date_from: from, date_until: to };
      patch.date_range_type = "range";
    } else {
      patch.data_load = true;
      patch.kpi_load = true;
    }
    dispatch({ type: UPDATE_STATE, payload: { app_state: patch, ui_state: { showspinner: true, isLock: false } } });

    setShowHistory(false);
    setMode("mini");   // keep the answer visible alongside the combined view
    flashWorkspace();
  };

  // Pulse a highlight ring over the workspace so the user sees where they landed.
  const flashWorkspace = () => {
    try {
      setTimeout(() => {
        const el =
          document.querySelector(".work-space") ||
          document.querySelector(".workspace-chart-container") ||
          document.querySelector(".workspace-table-container");
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.width < 40 || r.height < 40) return;
        setFlash({ top: r.top, left: r.left, width: r.width, height: r.height });
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlash(null), 1500);
      }, 160);
    } catch (_) {}
  };

  // ---- Stream failure handling -------------------------------------------
  // A silent retry is only safe when NOTHING reached the screen; once text has
  // streamed, retrying would duplicate the answer (and pay for it twice), so we
  // keep what arrived and offer a manual retry instead.
  const RETRYABLE = new Set(["first_frame", "stall", "network"]);
  const canRetrySilently = (info) =>
    !!info && !info.streamed && (RETRYABLE.has(info.reason)
      || (info.reason === "http" && Number(info.status) >= 500));

  // Dry, not zany — this is a revenue tool, and the user has just lost time.
  const FAIL_LINES = [
    "That thought got lost in transit. I tried twice — want me to have another go?",
    "I drew a blank: the connection dropped mid-sentence, twice. Shall we retry?",
    "Two attempts, no answer. Even the best forecasts miss sometimes — try again?",
  ];
  const failLine = () => FAIL_LINES[Math.floor(Math.random() * FAIL_LINES.length)];

  const send = (text, source = "typed") => {
    if (compareMode && abTester) return sendCompare(text);
    const q = (text !== undefined && text !== null ? text : input).trim();
    if (!q || loading) return;
    if (voiceRef.current) voiceRef.current();   // AL-59: never leave the mic open after sending
    if (deepLinkNote) setDeepLinkNote("");      // AL-71: the notice served its purpose

    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";

    if (!ctx.propertyId) {
      setMessages((m) => [
        ...m,
        { role: "user", content: q },
        { role: "assistant", content: "Select a property first — I need one to pull your revenue data.", error: true },
      ]);
      return;
    }

    // Append the user message + an empty assistant bubble we stream into.
    // pfScope is stamped at SEND time, not read from state at render time: the
    // user can switch scope mid-thread, and an answer must keep saying which
    // scope produced it — including after a PDF export.
    const nowIso = new Date().toISOString();
    const pfScope = activePortfolio ? activePortfolio.name : "";
    setMessages((m) => [
      ...m,
      // The QUESTION carries the scope too, not just the answer: the teal bubble
      // is what tells you, scrolling back weeks later, which scope you asked in.
      { role: "user", content: q, created_at: nowIso, pfScope },
      { role: "assistant", content: "", created_at: nowIso, pfScope },
    ]);
    setLoading(true);
    setStatus("");
    setSuggestions([]);
    setToolHints([]);
    setShowTools(false);
    setShowResume(false);
    setFbCommentFor(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const patchLast = (patch) =>
      setMessages((m) => {
        const copy = m.slice();
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", ...patch };
        return copy;
      });

    runStream(q, source, 0, patchLast);
  };

  // One streaming attempt. `attempt` 0 = first try; a silent retry re-enters with 1.
  const runStream = (q, source, attempt, patchLast) => {
    const controller = new AbortController();
    abortRef.current = controller;
    let acc = "";
    const tw = makeTypewriter((text) => patchLast({ content: text }));

    streamChat({
      propertyId: ctx.propertyId,
      // Portfolio scope, when one is picked (AL-80). Deliberately NOT sent by the
      // view-insight or AB-compare calls: a view descriptor is one tile on one
      // property, and compare mode is per-property model testing.
      portfolioId: activePortfolio ? activePortfolio.id : undefined,
      message: q,
      sessionId: sessionRef.current,
      source,
      signal: controller.signal,
      onStatus: (s) => setStatus(s),
      onSuggestions: (list) => {
        setSuggestions(Array.isArray(list) ? list.slice(0, 3) : []);
        setShowSuggestions(false); // arrive collapsed; the toggle shows the count
      },
      onSources: (list) => patchLast({ sources: Array.isArray(list) ? list : [] }),
      onSupport: (info) => patchLast({ support: info, complaintText: q }),
      onToolHints: (list) => setToolHints(Array.isArray(list) ? list.slice(0, 2) : []),
      onWidget: (w) => setMessages((m) => {
        const copy = m.slice();
        const last = copy[copy.length - 1];
        // One widget of a kind per answer: a re-emitted widget (e.g. the model
        // corrected the date) replaces the earlier card instead of stacking.
        const kept = (last.widgets || []).filter((x) => (x || {}).type !== (w || {}).type);
        copy[copy.length - 1] = { ...last, role: "assistant", widgets: [...kept, w] };
        return copy;
      }),
      onDelta: (d) => {
        acc += d;
        if (acc === d) setStatus(""); // clear the status once the answer starts
        tw.push(d);
      },
      onDone: (sid, messageId, _meta, engine) => {
        tw.snap();                    // feedback row/PDF/copy read the final DOM
        if (sid) {
          sessionRef.current = sid;
          setSessionId(sid);
        }
        // Attach the DB id so the thumbs become active, and stamp the arrival time.
        patchLast({ id: messageId || undefined, created_at: new Date().toISOString(), feedback: "", engine: engine || "" });
        setStatus("");
        setLoading(false);
        loadSessions();
      },
      onError: (msg, info) => {
        tw.abort();                   // the drain must not overwrite what we set below
        // Nothing on screen yet and the failure looks transient? Try once more,
        // and say so — a silent stall followed by a sudden answer is unnerving.
        if (attempt === 0 && canRetrySilently(info)) {
          setStatus("Connection hiccup — trying again…");
          setTimeout(() => runStream(q, source, 1, patchLast), 800);
          return;
        }
        const partial = (info && info.streamed) || !!acc;
        patchLast({
          // keep whatever streamed; the note renders underneath rather than wiping it
          content: partial ? acc : failLine(),
          error: true,
          interrupted: partial,
          failDetail: msg || "Network error",
          retryQuestion: q,          // powers the Retry button
          retrySource: source,
        });
        setStatus("");
        setLoading(false);
      },
      onAbort: ({ streamed } = {}) => {
        tw.abort();                   // freeze at what the user saw when they stopped
        // The caller pulled the plug: user hit stop, closed the window, switched
        // session. This used to return silently and leave `loading` true forever
        // — exactly the "spinner never stops" symptom.
        if (streamed || acc) {
          patchLast({ content: acc, stopped: true, retryQuestion: q, retrySource: source });
        } else {
          patchLast({ content: "Stopped before I got going.", stopped: true,
                      retryQuestion: q, retrySource: source });
        }
        setStatus("");
        setLoading(false);
      },
    });
  };

  // User-facing stop (the send button becomes a square while streaming), and the
  // retry action offered on a failed or stopped answer.
  const stopStream = () => {
    if (!loading) return;
    try { abortActiveStream(); } catch (_) {}
    try { if (window.analyze && window.analyze.track) window.analyze.track("askq_stop", {}); } catch (_) {}
  };

  const retryMessage = (m, idx) => {
    if (loading || !m || !m.retryQuestion) return;
    setMessages((prev) => prev.filter((_, i) => i !== idx));   // drop the failed bubble
    setTimeout(() => send(m.retryQuestion, m.retrySource || "retry"), 0);
  };

  /* Copy this answer WITH the question that produced it.
   *
   * Pasting an answer alone loses what was asked, so the recipient gets numbers
   * with no subject — the reader has to be told the question separately. Walking
   * back to the nearest preceding user message rebuilds the exchange.
   *
   * Markdown headings (not bold) label the two halves: `contentToHtml` renders
   * "## " as a real <h2>, so this pastes into Word/email as a structured
   * question-and-answer rather than one undifferentiated block.
   */
  const copyQa = (i) => {
    const answer = messages[i];
    let question = null;
    for (let k = i - 1; k >= 0; k -= 1) {
      if (messages[k] && messages[k].role === "user") { question = messages[k]; break; }
    }
    const parts = [];
    if (question && question.content) parts.push(`## Question\n\n${question.content}`);
    parts.push(`## Answer\n\n${(answer && answer.content) || ""}`);
    // The as-of stamp travels with the numbers — a pasted figure with no date is
    // exactly the ambiguity every payload in this product works to avoid.
    if (answer && answer.context_as_of) {
      parts.push(`_Data as of ${shortDate(answer.context_as_of)} · Ask LodgIQ_`);
    }
    copyMessage(parts.join("\n\n"), i);
  };

  // Rich copy: put both HTML (so Word renders real tables/headings/bold) and plain
  // text on the clipboard, with graceful fallbacks for older / non-secure contexts.
  const copyMessage = (text, i) => {
    const html = contentToHtml(text);
    const done = () => {
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1500);
    };

    // Fallback 1: rich copy via a temporary contenteditable + execCommand.
    const richFallback = () => {
      try {
        const div = document.createElement("div");
        div.setAttribute("contenteditable", "true");
        div.style.position = "fixed";
        div.style.left = "-9999px";
        div.style.opacity = "0";
        div.innerHTML = html || escapeHtml(text);
        document.body.appendChild(div);
        const range = document.createRange();
        range.selectNodeContents(div);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        const ok = document.execCommand("copy");
        sel.removeAllRanges();
        document.body.removeChild(div);
        if (ok) return done();
      } catch (_) {}
      plainFallback();
    };

    // Fallback 2: plain text only.
    const plainFallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
      } catch (_) {}
    };

    try {
      if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        });
        navigator.clipboard.write([item]).then(done).catch(richFallback);
        return;
      }
    } catch (_) {}
    richFallback();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" && loading) {   // Esc stops generating, like the button
      e.preventDefault();
      stopStream();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ---- Feedback (thumbs up/down + optional comment) ----
  const updateMsg = (idx, patch) =>
    setMessages((m) => m.map((msg, i) => (i === idx ? { ...msg, ...patch } : msg)));

  // Toggle & switch: same thumb clears the vote, the other thumb switches it.
  // Optimistic UI; reverted if the API call fails.
  const vote = (m, idx, dir) => {
    if (!m.id || !ctx.propertyId) return;
    const next = m.feedback === dir ? "" : dir;
    const prev = { feedback: m.feedback || "", feedback_comment: m.feedback_comment || "" };
    updateMsg(idx, next ? { feedback: next } : { feedback: "", feedback_comment: "" });
    if (next) {
      setFbCommentFor(m.id);
      setFbCommentText(m.feedback_comment || "");
    } else if (fbCommentFor === m.id) {
      setFbCommentFor(null);
      setFbCommentText("");
    }
    sendFeedback(ctx.propertyId, m.id, next, next ? m.feedback_comment || "" : "").catch(() => updateMsg(idx, prev));
  };

  // Report a technical issue the user raised (canned "complaint" reply → support card).
  const reportIssue = (m, idx) => {
    if (!m || !m.support || m.supportReporting || m.supportReported) return;
    updateMsg(idx, { supportReporting: true });
    let view = null;
    try {
      const u = state?.app?.current_state?.ui_state || {};
      view = { tiles: u.active_tiles || [], date_from: app_state.primary_stay_range?.date_from, date_until: app_state.primary_stay_range?.date_until };
    } catch (_) {}
    sendSupportRequest(ctx.propertyId, { message: m.complaintText || "", sessionId: sessionRef.current, view })
      .then(() => updateMsg(idx, { supportReporting: false, supportReported: true }))
      .catch(() => updateMsg(idx, { supportReporting: false, supportError: true }));
  };

  const submitFbComment = (m, idx) => {
    if (!m.id || !m.feedback || fbSaving) return;
    const text = fbCommentText.trim();
    setFbSaving(true);
    sendFeedback(ctx.propertyId, m.id, m.feedback, text)
      .then(() => updateMsg(idx, { feedback_comment: text }))
      .catch(() => {})
      .finally(() => {
        setFbSaving(false);
        setFbCommentFor(null);
        setFbCommentText("");
      });
  };

  const autoGrow = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
  };

  // ---- AL-59: dictation ------------------------------------------------
  // The mic only fills the box; sending stays a deliberate click/Enter so a
  // misheard rate never reaches the model on its own.
  const voiceBaseRef = useRef("");     // text already in the box when the mic opened
  const voiceTrack = (event, props) => {
    try { if (window.analyze && window.analyze.track) window.analyze.track(event, props || {}); } catch (_) {}
  };
  const applyVoiceText = (text, { final } = {}) => {
    const base = voiceBaseRef.current;
    const spoken = final ? fixTranscript(text) : text;      // repair only the settled text
    const next = base ? `${base.replace(/\s+$/, "")} ${spoken}` : spoken;
    setInput(next);
    const ta = taRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
    }
  };
  const voice = useVoiceInput({
    onInterim: (text) => applyVoiceText(text, { final: false }),
    onFinal: (text, meta) => {
      applyVoiceText(text, { final: true });
      voiceTrack("askq_voice_transcript", {
        chars: text.length, duration_ms: (meta && meta.durationMs) || 0,
        property_id: ctx.propertyId,
      });
      if (taRef.current) taRef.current.focus();
    },
  });
  const toggleVoice = () => {
    if (!voice.listening) {
      voiceBaseRef.current = input;                          // dictate onto what's typed
      voiceTrack("askq_voice_start", { property_id: ctx.propertyId });
    }
    voice.toggle();
  };
  useEffect(() => {
    if (voice.error) voiceTrack("askq_voice_error", { reason: voice.error });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.error]);
  // `send` runs before this hook is declared, so it reaches the stopper via a ref
  voiceRef.current = voice.listening ? voice.stop : null;
  // closing / collapsing the window must release the microphone
  useEffect(() => {
    if (mode === "closed" && voice.listening) voice.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, voice.listening]);
  const voiceHint = voice.error === "denied"
    ? "Microphone blocked — allow it in your browser's site settings to dictate."
    : voice.error === "nospeech"
      ? "Didn't catch that — try again, closer to the mic."
      : voice.error === "failed"
        ? "Dictation didn't start. You can still type your question."
        : "";

  // Hidden entirely for users without access (admin-controlled).
  if (!askEnabled) return null;

  const flashEl = flash ? (
    <div
      className="wiz-flash"
      style={{ top: flash.top, left: flash.left, width: flash.width, height: flash.height }}
      aria-hidden="true"
    />
  ) : null;

  // AL-71: switch-hotel confirmation for email deep links. Rendered in BOTH
  // return branches — the mismatch usually happens while the chat is closed.
  const deepLinkDialogEl = (
    <DeepLinkDialog
      deepLinkAsk={deepLinkAsk}
      ctx={ctx}
      theme={theme}
      onAccept={acceptDeepLinkSwitch}
      onDecline={declineDeepLinkSwitch}
      onDismiss={dismissDeepLinkAsk}
    />
  );

  if (mode === "closed") {
    const chatActive = messages.length > 0;
    // Clamp a stored position against the current viewport (window may have resized).
    const lp = launcherPos ? clampPos(launcherPos.x, launcherPos.y) : null;
    return (
      <>
        {flashEl}
        {deepLinkDialogEl}
        <button
          className={`wiz-launcher ${chatActive ? "wiz-launcher--active" : ""}`}
          style={lp ? { left: lp.x, top: lp.y, right: "auto", bottom: "auto" } : undefined}
          // Opening the chat straight from an open app dropdown hits the same
          // focus trap — clear it before the panel mounts and autofocuses.
          onMouseDownCapture={() => { dismissAppOverlays(); }}
          onMouseDown={onLauncherMouseDown}
          onClick={() => {
            // A drag ends with a click event — don't open the chat for drags.
            if (launcherDragRef.current.moved) { launcherDragRef.current.moved = false; return; }
            open();
          }}
          aria-label={chatActive ? "Resume Ask LodgIQ conversation" : "Open Ask LodgIQ"}
          title={chatActive ? "You have an active conversation" : "Drag to reposition"}
        >
          <span className="wiz-launcher__spark">{Icon.sparkle}</span>
          Ask LodgIQ
          <span className="wiz-beta">Beta</span>
          {briefBadge && <span className="wiz-launcher__brief" title="Your morning briefing is ready" />}
        </button>
      </>
    );
  }

  const isMax = mode === "max";
  const isMini = mode === "mini";
  const hasMessages = messages.length > 0;
  const compact = !isMax && width < 520;
  // AL-47: the "?" must do something on EVERY screen. Mid-session it opens the panel.
  // On the welcome screen it toggles the inline gallery — which the resume card hides
  // by default, so with a resume card "?" is the only way back to the examples
  // (hidden -> shown), and without one it simply collapses them (shown -> hidden).
  const emptyGalleryOpen = showResume ? showGallery : !showGallery;
  // Opener on a fresh start; "follow-up" only once the conversation is going.
  const cmpCount = (compareSel.length ? compareSel : abProfiles.slice(0, 2)).length;
  const inputPlaceholder = compareMode && abTester
    ? `Compare mode · ask once, run on ${cmpCount} model${cmpCount === 1 ? "" : "s"}…`
    : hasMessages
      ? "Ask a follow-up question…"
      : (activePortfolio
          ? `Ask about ${activePortfolio.name}…`
          : (ctx.propertyName ? `Ask about ${ctx.propertyName}…` : "Ask about your revenue data…"));

  // Innovative, time-aware greeting for the empty state.
  const firstName = (ctx.userName || "").trim().split(/\s+/)[0] || "there";
  const _hr = new Date().getHours();
  const timeGreet = _hr < 5 ? "Still up" : _hr < 12 ? "Good morning" : _hr < 17 ? "Good afternoon" : "Good evening";
  const greeting = `${timeGreet}, ${firstName}`;
  // The greeting must name the SCOPE the next answer will cover — in portfolio
  // mode it named the single hotel, which flatly contradicted the header (AL-80
  // follow-up). It also says which questions belong to which scope, since the
  // per-hotel decisions still route to one hotel.
  const greetingSub = activePortfolio
    ? `I'm your revenue co-pilot across ${activePortfolio.name} — ${activePortfolio.property_count} hotels. `
      + `Ask how the portfolio is doing, who's behind pace or missing budget. `
      + `Rate and restriction decisions are still per hotel — I'll ask which one.`
    : ctx.propertyName
      ? `I'm your revenue co-pilot for ${ctx.propertyName}. Ask me about pace, pricing, pickup, or what needs your attention today.`
      : "I'm your revenue co-pilot. Ask me about pace, pricing, pickup, or what needs your attention today.";

  // AL-94: one briefing element, two render sites (empty state and thread). The
  // SCOPE picks the component — a portfolio briefing REPLACES the hotel one
  // rather than appending to it, so one card never mixes the two grains.
  // Built here, below navigateToSource, because a const referenced before its
  // declaration throws on first render rather than reading as undefined.
  const briefingCard = !briefVisible ? null : (activePortfolio ? (
    <PortfolioBriefingCard
      briefing={briefStatus === "ready" ? brief : null}
      streamText={briefText}
      streaming={briefStatus === "streaming"}
      portfolioName={activePortfolio.name}
      hotelCount={activePortfolio.property_count}
      onOpenHotel={openBriefingHotel}
      onAskFollowUp={portfolioBriefFollowUp}
      onDismiss={dismissBriefing}
      onFeedback={briefingFeedback}
    />
  ) : (
    <BriefingCard
      briefing={briefStatus === "ready" ? brief : null}
      streamText={briefText}
      streaming={briefStatus === "streaming"}
      hotelName={ctx.propertyName}
      onAskFollowUp={briefingFollowUp}
      onDismiss={dismissBriefing}
      onFeedback={briefingFeedback}
      onOpenSource={navigateToSource}
    />
  ));

  return (
    <div className={`wiz-overlay ${isMax ? "wiz-overlay--max" : ""} ${isMini ? "wiz-overlay--mini" : ""}`} onMouseDown={(e) => { if (isMax && e.target === e.currentTarget) setMode("docked"); }}>
      {flashEl}
      {deepLinkDialogEl}
      <div
        className={`wiz-window ${isMax ? "wiz-window--max" : ""} ${isMini ? "wiz-window--mini" : ""} ${compact ? "wiz-window--compact" : ""} ${activePortfolio ? "wiz-window--pf" : ""}`}
        // An open MUI dropdown in the RM app traps focus and swallows our
        // keystrokes; close it before this press does anything else.
        onMouseDownCapture={onPanelMouseDownCapture}
        data-theme={theme}
        style={
          isMax
            ? (maxSize ? { width: maxSize.w, height: maxSize.h } : undefined)
            : isMini
              ? {
                  width,
                  ...(miniHeight ? { height: miniHeight } : null),
                  ...(miniPos ? { position: "fixed", left: miniPos.x, top: miniPos.y, right: "auto", bottom: "auto" } : null),
                }
              : { width }
        }
        role="dialog"
        aria-label="Ask LodgIQ"
      >
        {isMax && (
          <div className="wiz-corner" onMouseDown={onCornerResizeStart} title="Drag to resize" />
        )}
        {!isMax && !isMini && (
          <div className="wiz-resizer" onMouseDown={onResizeStart} title="Drag to resize" role="separator" aria-orientation="vertical" />
        )}
        {isMini && (
          <>
            <div className="wiz-resizer" onMouseDown={startMiniResize("w")} title="Drag to resize width" role="separator" aria-orientation="vertical" />
            <div className="wiz-mini-edge wiz-mini-edge--right" onMouseDown={startMiniResize("e")} title="Drag to resize width" role="separator" aria-orientation="vertical" />
            <div className="wiz-mini-edge wiz-mini-edge--top" onMouseDown={startMiniResize("n")} title="Drag to resize height" role="separator" aria-orientation="horizontal" />
            <div className="wiz-mini-edge wiz-mini-edge--bottom" onMouseDown={startMiniResize("s")} title="Drag to resize height" role="separator" aria-orientation="horizontal" />
            <div className="wiz-mini-grip wiz-mini-grip--nw" onMouseDown={startMiniResize("nw")} title="Drag to resize" aria-hidden="true" />
            <div className="wiz-mini-grip wiz-mini-grip--se" onMouseDown={startMiniResize("se")} title="Drag to resize" aria-hidden="true" />
          </>
        )}
        {/* Header — in mini mode it's also the drag handle for the floater */}
        <div
          className={`wiz-header ${isMax || width >= 470 ? "" : "wiz-header--tight"} ${activePortfolio ? "wiz-header--pf" : ""}`}
          onMouseDown={isMini ? onMiniHeaderDown : undefined}
          title={isMini ? "Drag to move" : undefined}
        >
          {/* Compact header: the ACTIVE SCOPE. One hotel, or a portfolio when the
              user picks one (AL-80) — a GM must never have to wonder which set of
              hotels an answer covered. No picker at all when they have none. */}
          <div className="wiz-header__brand">
            <span className="wiz-header__ic" aria-hidden="true">{Icon.aiSparkles}</span>
            {portfolios.length > 0 ? (
              <div className="wiz-ctx">
                <button
                  className={`wiz-ctx__btn ${activePortfolio ? "is-portfolio" : ""}`}
                  onClick={() => setShowCtxMenu((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={showCtxMenu}
                  title={activePortfolio
                    ? `${activePortfolio.name} — ${activePortfolio.property_count} hotels`
                    : (ctx.propertyName || "Ask LodgIQ")}
                >
                  {activePortfolio && (
                    <span className="wiz-ctx__glyph" aria-hidden="true">{Icon.buildings}</span>
                  )}
                  <span className="wiz-ctx__name">
                    {activePortfolio ? activePortfolio.name : (ctx.propertyName || "Ask LodgIQ")}
                  </span>
                  {activePortfolio && (
                    <span className="wiz-ctx__count">{activePortfolio.property_count}</span>
                  )}
                  <span className="wiz-ctx__caret" aria-hidden="true">{Icon.chevron}</span>
                </button>
                {showCtxMenu && (
                  <div className="wiz-ctx__menu" role="listbox">
                    <div className="wiz-ctx__head">Ask about</div>
                    <button
                      className={`wiz-ctx__opt ${!activePortfolio ? "is-sel" : ""}`}
                      role="option"
                      aria-selected={!activePortfolio}
                      onClick={() => { setActivePortfolio(null); setShowCtxMenu(false); }}
                    >
                      <span>{ctx.propertyName || "This hotel"}</span>
                      <span className="wiz-ctx__opt-meta">this hotel</span>
                    </button>
                    {portfolios.map((p) => (
                      <button
                        key={p.id}
                        className={`wiz-ctx__opt ${activePortfolio && activePortfolio.id === p.id ? "is-sel" : ""} ${p.within_cap ? "" : "is-disabled"}`}
                        role="option"
                        aria-selected={!!(activePortfolio && activePortfolio.id === p.id)}
                        disabled={!p.within_cap}
                        // Over-cap portfolios are shown but not selectable, with the
                        // reason on the row — quietly hiding them looks like a bug.
                        title={p.within_cap ? "" : `Too many hotels for one answer (${p.property_count})`}
                        onClick={() => {
                          setActivePortfolio(p);
                          setShowCtxMenu(false);
                          try {
                            window.analyze && window.analyze.track("askq_portfolio_mode", {
                              portfolio_size: p.property_count,
                            });
                          } catch (_) {}
                        }}
                      >
                        <span>{p.name}</span>
                        <span className="wiz-ctx__opt-meta">
                          {p.property_count} hotel{p.property_count === 1 ? "" : "s"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="wiz-header__name" title={ctx.propertyName || "Ask LodgIQ"}>
                {ctx.propertyName || <>Ask&nbsp;LodgIQ<span className="wiz-beta">Beta</span></>}
              </div>
            )}
          </div>
          <div className="wiz-header__actions">
            {messages.length > 0 && (
              <button className="wiz-btn" onClick={exportPdf}
                aria-label="Download conversation as PDF"
                data-tip="Download PDF">
                {Icon.download}
              </button>
            )}
            <div className="wiz-cmp-wrap">
              <button
                className={`wiz-btn ${showPrefMenu ? "wiz-btn--on" : ""}`}
                onClick={() => setShowPrefMenu((v) => !v)}
                aria-label="Settings"
                data-tip="Settings"
              >
                {Icon.gear}
              </button>
              {showPrefMenu && (
                <div className="wiz-cmp-picker">
                  <div className="wiz-cmp-picker__head">"Summarize this view" appears in</div>
                  <button
                    className={`wiz-pref-opt ${insightPref === "chat" ? "is-sel" : ""}`}
                    onClick={() => chooseInsightPref("chat")}
                  >
                    Ask LodgIQ chat window
                  </button>
                  <button
                    className={`wiz-pref-opt ${insightPref === "inline" ? "is-sel" : ""}`}
                    onClick={() => chooseInsightPref("inline")}
                  >
                    Inline panel over the view
                  </button>
                  <div className="wiz-cmp-picker__head" style={{ marginTop: 8 }}>Morning briefing</div>
                  {/* Binary preference -> a switch. Radio pairs like "Show / Off"
                      read as a choice between two unrelated things (AL-91). */}
                  <button
                    className="wiz-pref-toggle"
                    onClick={() => chooseBriefPref(!briefPref)}
                    role="switch"
                    aria-checked={!!briefPref}
                  >
                    <span>Show daily briefing</span>
                    <span className={`wiz-switch ${briefPref ? "is-on" : ""}`} aria-hidden="true"><i /></span>
                  </button>
                  {gallery.length > 0 && (
                    <>
                      <div className="wiz-cmp-picker__sep" />
                      <button
                        className="wiz-pref-opt"
                        onClick={() => { setShowPrefMenu(false); setShowGallery(true); }}
                      >
                        What can I ask?
                      </button>
                    </>
                  )}
                  <div className="wiz-cmp-picker__foot">Saved to your profile</div>
                </div>
              )}
            </div>
            {/* A/B compare is property-only. It sends no scope, so in portfolio
                mode every variant would answer from one hotel and be compared as
                if it were a portfolio answer — a misleading result on an internal
                tool. Hidden rather than fixed: not worth the wiring. */}
            {abTester && !activePortfolio && (
              <div className="wiz-cmp-wrap">
                <button
                  className={`wiz-btn ${compareMode ? "wiz-btn--on" : ""}`}
                  onClick={() => { setCompareMode((v) => !v); setShowComparePicker((v) => (compareMode ? false : v)); }}
                  aria-label="Compare models"
                  data-tip="Compare models (internal)"
                >
                  {Icon.columns}
                </button>
                {compareMode && (
                  <button
                    className={`wiz-btn wiz-cmp-gear ${showComparePicker ? "wiz-btn--on" : ""}`}
                    onClick={() => setShowComparePicker((v) => !v)}
                    aria-label="Choose models"
                    data-tip="Choose models"
                  >
                    {Icon.chevron}
                  </button>
                )}
                {compareMode && showComparePicker && (
                  <div className="wiz-cmp-picker">
                    <div className="wiz-cmp-picker__head">Compare up to 4 models</div>
                    {abProfiles.map((p) => {
                      const on = compareSel.includes(p.key);
                      const disabled = !on && compareSel.length >= 4;
                      return (
                        <label key={p.key} className={`wiz-cmp-opt ${disabled ? "is-disabled" : ""}`}>
                          <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggleCompareSel(p.key)} />
                          <span className="wiz-cmp-opt__label">{p.label}</span>
                          <span className="wiz-cmp-opt__meta">{p.vendor}</span>
                        </label>
                      );
                    })}
                    <div className="wiz-cmp-picker__foot">Internal only · not shown to customers</div>
                  </div>
                )}
              </div>
            )}
            <button className={`wiz-btn ${showHistory ? "wiz-btn--on" : ""}`} onClick={toggleHistory} aria-label="History" data-tip="History">{Icon.history}</button>
            <button className="wiz-btn wiz-btn--primary" onClick={newChat} aria-label="New chat" data-tip="New chat">
              {Icon.plus}<span className="wiz-btn__label">New</span>
            </button>
            <span className="wiz-head-sep" aria-hidden="true" />
            {/* Window SIZE — one segmented control, three explicit states. The
                pressed one is filled, so the header always says where you are
                (the old single toggle looked identical docked vs full). */}
            <div className="wiz-sizeseg" role="group" aria-label="Panel size">
              <button
                className={`wiz-sizeseg__btn ${isMini ? "is-on" : ""}`}
                onClick={() => chooseSize("mini")}
                aria-label="Half height" aria-pressed={isMini} data-tip="Half">
                {Icon.sizeHalf}
              </button>
              <button
                className={`wiz-sizeseg__btn ${!isMini && !isMax ? "is-on" : ""}`}
                onClick={() => chooseSize("docked")}
                aria-label="Docked panel" aria-pressed={!isMini && !isMax} data-tip="Docked">
                {Icon.sizeDocked}
              </button>
              <button
                className={`wiz-sizeseg__btn ${isMax ? "is-on" : ""}`}
                onClick={() => chooseSize("max")}
                aria-label="Full screen" aria-pressed={isMax} data-tip="Full">
                {Icon.sizeFull}
              </button>
            </div>
            <button
              className="wiz-btn wiz-btn--close"
              onClick={closeWindow}
              aria-label="Close"
              data-tip="Close"
            >
              {Icon.close}
            </button>
          </div>
        </div>

        {/* AL-94 scope strip. Portfolio mode is the only state where the answer
            covers hotels the user cannot see named anywhere else, so the strip
            lists them — and gives a one-click way back to a single hotel. It is
            also where an excluded hotel would be shown struck through rather
            than silently vanishing from the totals. */}
        {activePortfolio && !isMini && (
          <div className="wiz-scope">
            <span className="wiz-scope__ic" aria-hidden="true">{Icon.buildings}</span>
            <span className="wiz-scope__names" title={(activePortfolio.hotels || []).join(", ")}>
              Portfolio view
              {(activePortfolio.hotels || []).length > 0
                ? ` · ${activePortfolio.hotels.join(", ")}`
                : ` · ${activePortfolio.property_count} hotels`}
            </span>
            <button className="wiz-scope__exit" onClick={() => setActivePortfolio(null)}>
              Switch to one hotel
            </button>
          </div>
        )}

        {/* History panel */}
        {showHistory && (
          <div className="wiz-history">
            <div className="wiz-history__head">
              <span>RECENT CONVERSATIONS</span>
              {sessions.length > 0 && (
                <button className="wiz-history__clear" onClick={clearAll}>Clear all</button>
              )}
            </div>
            {sessions.length === 0 ? (
              <div className="wiz-history__empty">No past conversations yet.</div>
            ) : (
              <ul className="wiz-history__list">
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    className={`wiz-history__item ${s.id === sessionId ? "is-active" : ""}`}
                    onClick={() => openSession(s.id)}
                  >
                    <div className="wiz-history__row">
                      <span className="wiz-history__title">
                        {s.title || `Conversation ${s.id}`}
                        {/* AL-80: a portfolio conversation reopened later must not
                            read as if it were about this one hotel. */}
                        {s.portfolio_name && (
                          <span className="wiz-history__scope" title={`Asked across ${s.portfolio_name}`}>
                            {s.portfolio_name}
                          </span>
                        )}
                      </span>
                      <span className="wiz-history__ago" title={`Last updated ${fullStamp(s.updated_at)}`}>
                        {timeAgo(s.updated_at)}
                      </span>
                    </div>
                    <span className="wiz-history__started" title={`Started ${fullStamp(s.created_at)}`}>
                      Started {shortDate(s.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Body */}
        <div className="wiz-body" ref={bodyRef}>
          <BgLines />
          {/* AL-47: "?" gallery panel — the same themed examples, re-openable mid-session */}
          {showGallery && hasMessages && (
            <div className="wiz-gallery wiz-gallery--panel">
              <div className="wiz-gallery__head">
                <span className="wiz-gallery__title">What you can ask</span>
                <button className="wiz-gallery__close" onClick={() => setShowGallery(false)}
                  aria-label="Close examples">✕</button>
              </div>
              {gallery.map((g) => (
                <div className="wiz-gallery__theme" key={g.theme}>
                  <span className="wiz-gallery__label">{g.theme}</span>
                  <div className="wiz-gallery__chips">
                    {g.examples.map((ex, i) => (
                      <button key={i} className="wiz-chip"
                        onClick={() => { setShowGallery(false); send(ex, "suggestion"); }}>{ex}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!hasMessages && !loading ? (
            <div className="wiz-empty">
              <span className="wiz-empty__spark">{Icon.aiSparkles}</span>
              <h2 className="wiz-empty__title">{greeting}</h2>
              <p className="wiz-empty__desc">{greetingSub}</p>

              {/* AL-47: capability-aware example gallery — generated per property,
                  never hand-maintained; clicks log as source='suggestion' */}
              {emptyGalleryOpen && gallery.length > 0 && (
                <div className="wiz-gallery">
                  {gallery.slice(0, 4).map((g) => (
                    <div className="wiz-gallery__theme" key={g.theme}>
                      <span className="wiz-gallery__label">{g.theme}</span>
                      <div className="wiz-gallery__chips">
                        {g.examples.slice(0, 2).map((ex, i) => (
                          <button key={i} className="wiz-chip" onClick={() => send(ex, "suggestion")}>{ex}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {briefingCard}

              {showResume && sessions.length > 0 && (
                <div className="wiz-resume">
                  <div className="wiz-resume__label">Pick up where you left off</div>
                  <div className="wiz-resume__title">{sessions[0].title || `Conversation ${sessions[0].id}`}</div>
                  <div className="wiz-resume__meta">Updated {timeAgo(sessions[0].updated_at)} · started {shortDate(sessions[0].created_at)}</div>
                  <div className="wiz-resume__actions">
                    <button className="wiz-resume__continue" onClick={() => openSession(sessions[0].id)}>Continue</button>
                    <button className="wiz-resume__new"
                      onClick={() => { setShowResume(false); setShowGallery(false); }}>Start new chat</button>
                  </div>
                  {sessions.length > 1 && (
                    <button className="wiz-resume__more" onClick={toggleHistory}>
                      View all {sessions.length} conversations
                    </button>
                  )}
                </div>
              )}

              {scopedTools.length > 0 && (
              <div className="wiz-tools-intro">
                {briefVisible && !toolsIntroOpen ? (
                  <button className="wiz-tools-intro__toggle" onClick={() => setToolsIntroOpen(true)}>
                    <span className="wiz-tools-intro__toggle-ic" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                    </span>
                    Start with a tool
                    <span className="wiz-tools-intro__toggle-chev" aria-hidden="true">{Icon.chevron}</span>
                  </button>
                ) : (
                  <>
                    <div className="wiz-tools-intro__label">Or start with a tool</div>
                    <div className="wiz-tools-intro__grid">
                      {scopedTools.map((t) => (
                        <button key={t.key} className="wiz-tool-card" onClick={() => send(t.prompt, "tool_menu")}>
                          <span className="wiz-tool-card__ic">{t.icon}</span>
                          <span className="wiz-tool-card__txt">
                            <span className="wiz-tool-card__name">{t.label}</span>
                            <span className="wiz-tool-card__desc">{t.desc}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          ) : (
            <div className="wiz-messages">
              {loadedHistory && (
                <div className="wiz-freshness-banner">
                  This is an earlier conversation. Answers below reflect the data available then —
                  ask a new question to use the latest.
                </div>
              )}
              {briefingCard}
              {messages.map((m, i) => {
                if (m.role === "compare") {
                  return (
                    <div key={i} className="wiz-msg wiz-msg--compareturn">
                      <div className="wiz-msg wiz-msg--user">
                        <div className="wiz-msg__bubble">{renderContent(m.question, theme)}</div>
                      </div>
                      <div
                        className="wiz-compare"
                        style={{ gridTemplateColumns: `repeat(${m.variants.length}, minmax(240px, 1fr))` }}
                      >
                        {m.variants.map((v, vi) => {
                          const pending = !v.content && !v.error && !v.done;
                          return (
                            <div className="wiz-compare__col" key={v.key}>
                              <div className="wiz-compare__head">
                                <span className="wiz-compare__label" title={v.model || v.key}>{v.label}</span>
                                <span className="wiz-compare__stats">
                                  {v.done && !v.error && v.latencyMs != null ? `${(v.latencyMs / 1000).toFixed(1)}s` : ""}
                                  {v.done && !v.error && v.tokens ? ` · ${v.tokens} tok` : ""}
                                </span>
                              </div>
                              <div className="wiz-compare__body">
                                {pending ? (
                                  <span className="wiz-thinking">
                                    {v.status ? <span className="wiz-status">{v.status}</span> : null}
                                    <span className="wiz-typing"><span /><span /><span /></span>
                                  </span>
                                ) : v.error ? (
                                  <div className="wiz-msg__bubble wiz-msg__bubble--error">{v.error}</div>
                                ) : (
                                  renderContent(v.content, theme)
                                )}
                              </div>
                              {v.done && !v.error && (
                                <div className="wiz-compare__foot">
                                  <span className="wiz-stars" title="Rate this answer">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                      <button
                                        key={s}
                                        className={`wiz-star ${v.rating >= s ? "is-on" : ""}`}
                                        onClick={() => rateVariant(m.group, vi, v, s)}
                                        disabled={!v.resultId}
                                        aria-label={`Rate ${s} of 5`}
                                      >
                                        ★
                                      </button>
                                    ))}
                                  </span>
                                  <button
                                    className="wiz-compare__copy"
                                    onClick={() => copyMessage(v.content, `cmp-${m.group}-${vi}`)}
                                    aria-label="Copy answer"
                                    data-tip="Copy"
                                  >
                                    {Icon.copy}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                const isAI = m.role !== "user";
                const streaming = isAI && !m.content;
                const votable = isAI && m.id && m.content && !m.error;
                return (
                  <div key={i} className={`wiz-msg wiz-msg--${isAI ? "ai" : "user"} ${m.pfScope ? "is-pf" : ""}`}>
                    <div className={`wiz-msg__bubble ${m.error ? "wiz-msg__bubble--error" : ""}`}>
                      {streaming ? (
                        <span className="wiz-thinking">
                          {status && i === messages.length - 1 ? (
                            <span className="wiz-status">{status}</span>
                          ) : null}
                          <span className="wiz-typing"><span /><span /><span /></span>
                        </span>
                      ) : (
                        renderContent(m.content, theme)
                      )}
                      {/* interrupted / failed answers: say what happened and offer
                          one click to ask again (the question is kept on the message) */}
                      {(m.stopped || m.error) && m.retryQuestion && !streaming && (
                        <div className="wiz-msg__recover">
                          <span className="wiz-msg__recover-note">
                            {m.stopped
                              ? (m.interrupted || m.content ? "Stopped." : "")
                              : m.interrupted
                                ? "The answer was cut off mid-stream."
                                : ""}
                            {m.failDetail && <em title={m.failDetail}> ({m.failDetail})</em>}
                          </span>
                          <button className="wiz-msg__retry" onClick={() => retryMessage(m, i)}>
                            Retry
                          </button>
                        </div>
                      )}
                    </div>
                    {isAI && Array.isArray(m.widgets) && m.widgets.map((w, wi) => {
                      if (!w) return null;
                      if (w.type === "calculator") {
                        return (
                          <CalculatorCard
                            key={wi}
                            widget={w}
                            propertyId={ctx.propertyId}
                            onInterpret={(text) => send(text, "calculator")}
                          />
                        );
                      }
                      // The hidden scratch calculator — client-side only, no
                      // propertyId and no interpret hook: it isn't RM data.
                      if (w.type === "math_pad") {
                        return <MathPadCard key={wi} />;
                      }
                      if (w.type === "rate_simulator") {
                        return (
                          <RateSimulatorCard
                            key={wi}
                            widget={w}
                            propertyId={ctx.propertyId}
                            onInterpret={(text) => send(text, "calculator")}
                          />
                        );
                      }
                      if (w.type === "demand_calendar") {
                        return (
                          <DemandCalendarCard
                            key={wi}
                            widget={w}
                            propertyId={ctx.propertyId}
                            onOpenDate={(d) => navigateToSource({
                              id: "otb", domain: "otb", label: "On-The-Books",
                              params: { date_from: d, date_to: d, grain: "hotel" },
                            })}
                            onInterpret={(text) => send(text, "calculator")}
                          />
                        );
                      }
                      if (w.type === "pickup_sensitivity") {
                        return (
                          <PickupSensitivityCard
                            key={wi}
                            widget={w}
                            propertyId={ctx.propertyId}
                            onInterpret={(text) => send(text, "calculator")}
                          />
                        );
                      }
                      if (w.type === "group_quote") {
                        return (
                          <GroupQuoteCard
                            key={wi}
                            widget={w}
                            hotelName={ctx.propertyName}
                            onInterpret={(text) => send(text, "calculator")}
                          />
                        );
                      }
                      return null;
                    })}
                    {!streaming && (
                      <div className="wiz-msg__meta">
                        {/* AL-94: this answer covered a portfolio, not the hotel
                            in the header. Its own class, NOT wiz-msg__engine —
                            the engine glyph is internal chrome the PDF strips,
                            while scope is material to reading the numbers and
                            must survive export. On paper the glyph is useless
                            alone, so a text label takes over there. */}
                        {isAI && m.pfScope && (
                          <span className="wiz-msg__scope" title={`Answered across ${m.pfScope}`}>
                            <span className="wiz-msg__scope-ic" aria-hidden="true">{Icon.buildings}</span>
                            <span className="wiz-msg__scope-txt">Portfolio: {m.pfScope}</span>
                          </span>
                        )}
                        {isAI && ENGINE_GLYPH[m.engine] && (
                          <span
                            className={`wiz-msg__engine wiz-msg__engine--${m.engine}`}
                            aria-hidden="true"
                          >
                            {ENGINE_GLYPH[m.engine].icon}
                          </span>
                        )}
                        <button
                          className={`wiz-msg__copy ${copiedIdx === i ? "is-copied" : ""}`}
                          onClick={() => copyMessage(m.content, i)}
                          aria-label={copiedIdx === i ? "Copied" : "Copy message"}
                        >
                          {copiedIdx === i ? Icon.check : Icon.copy}
                          <span>{copiedIdx === i ? "Copied" : "Copy"}</span>
                        </button>
                        {isAI && m.content && (
                          <span className="wiz-msg__more"
                            ref={msgMenuIdx === i ? msgMenuRef : null}>
                            <button
                              className={`wiz-msg__morebtn ${msgMenuIdx === i ? "is-on" : ""}`}
                              onClick={() => setMsgMenuIdx((cur) => (cur === i ? null : i))}
                              aria-label="More actions"
                              aria-haspopup="menu"
                              aria-expanded={msgMenuIdx === i}
                              data-tip="More"
                            >
                              {Icon.dots}
                            </button>
                            {msgMenuIdx === i && (
                              <div className="wiz-msg__menu" role="menu">
                                <button
                                  className="wiz-msg__menuitem"
                                  role="menuitem"
                                  onClick={() => { setMsgMenuIdx(null); copyQa(i); }}
                                >
                                  <span className="wiz-msg__menuic">{Icon.messages}</span>
                                  <span>Copy question &amp; answer</span>
                                </button>
                              </div>
                            )}
                          </span>
                        )}
                        {m.created_at && (
                          <span className="wiz-msg__time" title={fullStamp(m.created_at)}>
                            {msgTime(m.created_at)}
                          </span>
                        )}
                        {isAI && m.content && m.context_as_of && (
                          <span className="wiz-msg__asof" title={`Grounded in data as of ${fullStamp(m.context_as_of)}`}>
                            {Icon.clock} data as of {shortDate(m.context_as_of)}
                          </span>
                        )}
                        {votable && (
                          <span className="wiz-fb">
                            <button
                              className={`wiz-fb__btn ${m.feedback === "up" ? "is-on is-up" : ""}`}
                              onClick={() => vote(m, i, "up")}
                              aria-label="Good answer"
                              data-tip={m.feedback === "up" ? "Remove rating" : "Good answer"}
                            >
                              {Icon.thumbUp}
                            </button>
                            <button
                              className={`wiz-fb__btn ${m.feedback === "down" ? "is-on is-down" : ""}`}
                              onClick={() => vote(m, i, "down")}
                              aria-label="Poor answer"
                              data-tip={m.feedback === "down" ? "Remove rating" : "Poor answer"}
                            >
                              {Icon.thumbDown}
                            </button>
                          </span>
                        )}
                      </div>
                    )}
                    {isAI && !streaming && Array.isArray(m.sources) && m.sources.some((s) => registryFor(s)) && (
                      <div className="wiz-sources">
                        <span className="wiz-sources__label">Sources</span>
                        {mergeTilesForSources(m.sources).length >= 2 && (
                          <button
                            className="wiz-source-chip wiz-source-chip--merge"
                            onClick={() => openCombinedView(m.sources)}
                            title="Open a combined IntelliMerge view of these sources"
                          >
                            <span className="wiz-source-chip__ic">{Icon.columns}</span>
                            Combined view
                          </button>
                        )}
                        {m.sources.filter((s) => registryFor(s)).map((s, si) => (
                          <button
                            key={si}
                            className="wiz-source-chip"
                            onClick={() => navigateToSource(s)}
                            title={`Open ${s.label} in the app to validate`}
                          >
                            {s.label}
                            <span className="wiz-source-chip__ic">{Icon.link}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {isAI && !streaming && m.support && (
                      <div className="wiz-support">
                        {m.supportReported ? (
                          <span className="wiz-support__done">Reported — our team will follow up.</span>
                        ) : (
                          <>
                            <button
                              className="wiz-support__report"
                              disabled={m.supportReporting}
                              onClick={() => reportIssue(m, i)}
                            >
                              {m.supportReporting ? "Reporting…" : "Report this issue"}
                            </button>
                            <a
                              className="wiz-support__mail"
                              href={`mailto:${m.support.email}?subject=${encodeURIComponent("LodgIQ RM — issue report")}`}
                            >
                              or email {m.support.email}
                            </a>
                            {m.supportError && <span className="wiz-support__err">Couldn't send — please email us.</span>}
                          </>
                        )}
                      </div>
                    )}
                    {votable && m.feedback && fbCommentFor === m.id && (
                      <div className="wiz-fb__commentbox">
                        <input
                          type="text"
                          value={fbCommentText}
                          maxLength={2000}
                          placeholder={m.feedback === "up" ? "What worked well? (optional)" : "What was wrong? (optional)"}
                          onChange={(e) => setFbCommentText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") submitFbComment(m, i); if (e.key === "Escape") setFbCommentFor(null); }}
                          autoFocus
                        />
                        <button className="wiz-fb__send" disabled={fbSaving} onClick={() => submitFbComment(m, i)}>
                          {fbSaving ? "…" : "Send"}
                        </button>
                        <button className="wiz-fb__skip" disabled={fbSaving} onClick={() => { setFbCommentFor(null); setFbCommentText(""); }}>
                          Skip
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wiz-footer">
          {toolHints.length > 0 && !loading && (
            <div className="wiz-toolhints">
              {toolHints.map((h, i) => {
                const t = TOOL_MENU.find((x) => h.label && h.label.toLowerCase().includes(x.key)) || null;
                return (
                  <button key={i} className="wiz-toolhint" onClick={() => send(h.prompt, "tool_hint")}>
                    <span className="wiz-toolhint__ic">{(t && t.icon) || Icon.sparkle}</span>
                    {h.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* AL-94: a per-hotel question asked in portfolio mode is answered with
              "which hotel?" — correct, but a dead end without a way to say which.
              Detected from the answer text rather than a backend contract: the
              refusal is free prose, and a false positive costs one helpful row
              of chips while a missed one costs the user a retype. */}
          {whichHotelChips && !loading && (
            <div className="wiz-whichhotel">
              <span className="wiz-whichhotel__lead">Ask about</span>
              <div className="wiz-whichhotel__chips">
                {(activePortfolio.hotels || []).map((h, i) => (
                  <button key={i} className="wiz-chip" onClick={() => answerWhichHotel(h)}>{h}</button>
                ))}
              </div>
            </div>
          )}

          {suggestions.length > 0 && !loading && (
            <div className="wiz-followups">
              <button
                className={`wiz-followups__toggle ${showSuggestions ? "is-open" : ""}`}
                onClick={() => setShowSuggestions((v) => !v)}
                aria-expanded={showSuggestions}
              >
                <span className="wiz-followups__spark">{Icon.sparkle}</span>
                Suggested follow-ups ({suggestions.length})
                <span className="wiz-followups__chev">{Icon.chevron}</span>
              </button>
              {showSuggestions && (
                <div className="wiz-followups__list">
                  {suggestions.map((s, i) => (
                    <button key={i} className="wiz-chip" onClick={() => { setShowSuggestions(false); send(s, "suggestion"); }}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {announcement && (
            <div className="wiz-announce" role="status">
              <span className="wiz-announce__badge">New</span>
              <span className="wiz-announce__text">{announcement.text}</span>
              <button className="wiz-announce__close" onClick={dismissAnnouncement}
                aria-label="Dismiss announcement" title="Dismiss">✕</button>
            </div>
          )}
          <div className="wiz-inputbar">
            {/* No "+" at all when the scope has no tools — an empty tray is worse
                than no button. Returns the moment portfolio tools exist. */}
            {scopedTools.length > 0 && (
            <div className="wiz-tools" ref={toolsRef}>
              <button
                className={`wiz-tools__btn ${showTools ? "is-on" : ""}`}
                onClick={() => setShowTools((v) => !v)}
                aria-label="Tools"
                aria-expanded={showTools}
                data-tip="Tools"
              >
                {Icon.plus}
              </button>
              {showTools && (
                <div className="wiz-tools__menu" role="menu">
                  {scopedTools.map((t) => (
                    <button key={t.key} className="wiz-tools__item" role="menuitem"
                      onClick={() => { setShowTools(false); send(t.prompt, "tool_menu"); }}>
                      <span className="wiz-tools__ic">{t.icon}</span>
                      <span className="wiz-tools__txt">
                        <span className="wiz-tools__name">{t.label}</span>
                        <span className="wiz-tools__desc">{t.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}
            <textarea
              ref={taRef}
              rows={1}
              value={input}
              placeholder={voice.listening ? "Listening… speak your question" : inputPlaceholder}
              onChange={autoGrow}
              onKeyDown={onKeyDown}
            />
            {voice.supported && (
              <button
                className={`wiz-mic ${voice.listening ? "is-live" : ""}`}
                onClick={toggleVoice}
                disabled={loading}
                aria-label={voice.listening ? "Stop dictation" : "Ask by voice"}
                aria-pressed={voice.listening}
                title={voice.listening ? "Stop dictation" : "Ask by voice"}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="9" y="3" width="6" height="11" rx="3" />
                  <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                </svg>
                {voice.listening && <span className="wiz-mic__pulse" aria-hidden="true" />}
              </button>
            )}
            {loading ? (
              /* while streaming, Send becomes Stop — the answer so far is kept */
              <button
                className="wiz-send wiz-send--stop"
                onClick={stopStream}
                aria-label="Stop generating"
                title="Stop generating (Esc)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor" />
                </svg>
              </button>
            ) : (
              <button
                className={`wiz-send ${input.trim() ? "wiz-send--active" : ""}`}
                disabled={!input.trim()}
                onClick={() => send()}
                aria-label="Send"
              >
                {Icon.send}
              </button>
            )}
          </div>

          {(voice.listening || voiceHint) && (
            <div className={`wiz-voicebar ${voiceHint ? "is-error" : ""}`} role="status" aria-live="polite">
              {voiceHint || "Listening… click the mic to stop. Review the text before sending."}
            </div>
          )}
          {deepLinkNote && !voice.listening && (
            <div className="wiz-voicebar wiz-voicebar--link" role="status">{deepLinkNote}</div>
          )}

          <div className="wiz-footnote">
            {compareMode && abTester
              ? "Compare mode (internal) · answers are logged for evaluation, not saved to chat history"
              : "Ask LodgIQ may make mistakes. Verify responses."}
          </div>
        </div>
      </div>
    </div>
  );
}
